/**
 * DLPixelH v2.0 - Refactored version
 * 
 * Main improvements:
 * 1. Modular design with clearer separation of responsibilities
 * 2. Simplified inheritance hierarchy
 * 3. Unified logging and error handling
 * 4. Clearer configuration model
 * 5. Better extensibility
 * 
 * Public API remains backward compatible:
 * - DLPixelH.capture(eventName, eventData)
 * - DLPixelH.identify(userId, userProperties)
 * - DLPixelH.setUserProperty(propertyName, propertyValue)
 * - DLPixelH.addToUserArrayProperty(propertyName, propertyValue)
 * 
 * Debugging and console modes:
 * 
 * 1. Debug Mode:
 *    - Purpose: print event data only, without reporting to any platform
 *    - Behavior: forcibly enables console mode
 *    - Setter: DLPixelH.setDebugMode(true/false)
 *    - URL param: ?dlpixel_debug=true/false
 *    - Persistence: automatically saved to localStorage and survives page refreshes
 * 
 * 2. Console Mode:
 *    - Purpose: control whether all logs are printed to the console
 *    - Behavior: independent from debug mode and can be toggled separately
 *    - Setter: DLPixelH.setConsoleMode(true/false)
 *    - URL param: ?dlpixel_console=true/false
 *    - Compatible param: ?dlpixel_log=true/false
 *    - Persistence: automatically saved to localStorage and survives page refreshes
 * 
 * 3. Event Logs Mode:
 *    - Purpose: control event-related logging only (capture, identify, setUserProperty, etc.)
 *    - Behavior: independent from system logs
 *    - Setter: DLPixelH.setEventLogsMode(true/false)
 *    - URL param: ?dlpixel_event_logs=true/false
 *    - Shortcut param: ?dlpixel_log=event or ?dlpixel_log=events
 *    - Persistence: automatically saved to localStorage and survives page refreshes
 * 
 * 4. System Logs Mode:
 *    - Purpose: control system-related logging only (initialization, SDK checks, etc.)
 *    - Behavior: independent from event logs
 *    - Setter: DLPixelH.setSystemLogsMode(true/false)
 *    - URL param: ?dlpixel_system_logs=true/false
 *    - Shortcut param: ?dlpixel_log=system
 *    - Persistence: automatically saved to localStorage and survives page refreshes
 * 
 * 5. SDK verbose logs:
 *    - Purpose: control detailed logging for SDK loading and validation
 *    - Setter: DLPixelH.setSDKVerbose(true/false)
 *    - URL param: ?dlpixel_sdk_verbose=true/false
 * 
 * 6. Storage management:
 *    - Clear settings: DLPixelH.clearStoredSettings()
 *    - View status: DLPixelH.getStorageInfo()
 *    - Storage keys: dlpixel_debug_mode, dlpixel_console_mode, dlpixel_event_logs, dlpixel_system_logs
 * 
 * Typical usage:
 * - Development debugging: ?dlpixel_debug=true (print only, no reporting, persisted)
 * - Event-focused logs: ?dlpixel_event_logs=true (event logs only)
 * - System-focused logs: ?dlpixel_system_logs=true (system logs only)
 * - Production monitoring: ?dlpixel_console=true (normal reporting with full console logs)
 * - SDK troubleshooting: ?dlpixel_log=sdk (enable console logs + SDK verbose logs)
 * - Reset settings: DLPixelH.clearStoredSettings() (restore default state)
 */

// ==================== Utilities ====================

/**
 * Unified logger
 */
class Logger {
    static isConsoleEnabled = false;
    static isDebugMode = false;

    // Category-specific log controls
    static eventLogsEnabled = false;    // Event-related logs (capture, identify, setUserProperty, etc.)
    static systemLogsEnabled = false;   // System logs (initialization, SDK checks, etc.)

    // localStorage keys
    static STORAGE_KEYS = {
        DEBUG_MODE: 'dlpixel_debug_mode',
        CONSOLE_MODE: 'dlpixel_console_mode',
        EVENT_LOGS: 'dlpixel_event_logs',
        SYSTEM_LOGS: 'dlpixel_system_logs'
    };

    /**
     * Load settings from localStorage
     * Optimization: load all settings first, then apply debug-mode overrides
     */
    static loadFromStorage() {
        try {
            if (typeof localStorage === 'undefined') return;

            // Load all base settings first
            const debugMode = localStorage.getItem(Logger.STORAGE_KEYS.DEBUG_MODE);
            const consoleMode = localStorage.getItem(Logger.STORAGE_KEYS.CONSOLE_MODE);
            const eventLogs = localStorage.getItem(Logger.STORAGE_KEYS.EVENT_LOGS);
            const systemLogs = localStorage.getItem(Logger.STORAGE_KEYS.SYSTEM_LOGS);

            // Apply console mode
            if (consoleMode !== null) {
                Logger.isConsoleEnabled = consoleMode === 'true';
            }

            // Apply category-specific logs first to preserve user preference
            if (eventLogs !== null) {
                Logger.eventLogsEnabled = eventLogs === 'true';
            }
            if (systemLogs !== null) {
                Logger.systemLogsEnabled = systemLogs === 'true';
            }

            // Handle debug mode last because it may override the settings above
            if (debugMode !== null) {
                Logger.isDebugMode = debugMode === 'true';
                if (Logger.isDebugMode) {
                    // Debug mode forces all logs on, while preserving category preferences for later restore
                    Logger.isConsoleEnabled = true;
                    Logger.eventLogsEnabled = true;
                    Logger.systemLogsEnabled = true;
                }
            }
        } catch (error) {
            // Fall back to defaults when localStorage is unavailable
        }
    }

    /**
     * Save settings to localStorage
     */
    static saveToStorage() {
        try {
            if (typeof localStorage === 'undefined') return;

            localStorage.setItem(Logger.STORAGE_KEYS.DEBUG_MODE, Logger.isDebugMode.toString());
            localStorage.setItem(Logger.STORAGE_KEYS.CONSOLE_MODE, Logger.isConsoleEnabled.toString());
            localStorage.setItem(Logger.STORAGE_KEYS.EVENT_LOGS, Logger.eventLogsEnabled.toString());
            localStorage.setItem(Logger.STORAGE_KEYS.SYSTEM_LOGS, Logger.systemLogsEnabled.toString());
        } catch (error) {
            // Ignore localStorage write failures
        }
    }

    /**
     * Clear settings stored in localStorage
     */
    static clearStorage() {
        try {
            if (typeof localStorage === 'undefined') return;

            localStorage.removeItem(Logger.STORAGE_KEYS.DEBUG_MODE);
            localStorage.removeItem(Logger.STORAGE_KEYS.CONSOLE_MODE);
            localStorage.removeItem(Logger.STORAGE_KEYS.EVENT_LOGS);
            localStorage.removeItem(Logger.STORAGE_KEYS.SYSTEM_LOGS);
        } catch (error) {
            // Ignore localStorage access failures
        }
    }

    /**
     * Get current storage status
     */
    static getStorageStatus() {
        try {
            if (typeof localStorage === 'undefined') return { available: false };

            return {
                available: true,
                debugMode: localStorage.getItem(Logger.STORAGE_KEYS.DEBUG_MODE),
                consoleMode: localStorage.getItem(Logger.STORAGE_KEYS.CONSOLE_MODE),
                eventLogs: localStorage.getItem(Logger.STORAGE_KEYS.EVENT_LOGS),
                systemLogs: localStorage.getItem(Logger.STORAGE_KEYS.SYSTEM_LOGS)
            };
        } catch (error) {
            return { available: false, error: error.message };
        }
    }

    static setConsoleEnabled(enabled) {
        Logger.isConsoleEnabled = enabled;
        Logger.saveToStorage();
    }

    static setDebugMode(enabled) {
        Logger.isDebugMode = enabled;
        if (enabled) {
            Logger.isConsoleEnabled = true;
            Logger.eventLogsEnabled = true;
            Logger.systemLogsEnabled = true;
        }
        Logger.saveToStorage();
    }

    /**
     * Toggle event logs
     */
    static setEventLogsEnabled(enabled) {
        Logger.eventLogsEnabled = enabled;
        Logger.saveToStorage();
    }

    /**
     * Toggle system logs
     */
    static setSystemLogsEnabled(enabled) {
        Logger.systemLogsEnabled = enabled;
        Logger.saveToStorage();
    }

    static log(source, ...args) {
        if (Logger.isConsoleEnabled) {
            console.log(`[${source}]`, ...args);
        }
    }

    static warn(source, ...args) {
        if (Logger.isConsoleEnabled) {
            console.warn(`[${source}]`, ...args);
        }
    }

    static error(source, ...args) {
        if (Logger.isConsoleEnabled) {
            console.error(`[${source}]`, ...args);
        }
    }

    /**
     * Event-related logs (capture, identify, setUserProperty, etc.)
     * Only printed in debug mode or when event logs are enabled
     */
    static eventLog(source, ...args) {
        if (Logger.isDebugMode || Logger.eventLogsEnabled) {
            console.log(`[${source}][EVENT]`, ...args);
        }
    }

    static eventWarn(source, ...args) {
        if (Logger.isDebugMode || Logger.eventLogsEnabled) {
            console.warn(`[${source}][EVENT]`, ...args);
        }
    }

    static eventError(source, ...args) {
        if (Logger.isDebugMode || Logger.eventLogsEnabled) {
            console.error(`[${source}][EVENT]`, ...args);
        }
    }

    /**
     * System-related logs (initialization, SDK checks, etc.)
     * Only printed in debug mode or when system logs are enabled
     */
    static systemLog(source, ...args) {
        if (Logger.isDebugMode || Logger.systemLogsEnabled) {
            console.log(`[${source}][SYSTEM]`, ...args);
        }
    }

    static systemWarn(source, ...args) {
        if (Logger.isDebugMode || Logger.systemLogsEnabled) {
            console.warn(`[${source}][SYSTEM]`, ...args);
        }
    }

    static systemError(source, ...args) {
        if (Logger.isDebugMode || Logger.systemLogsEnabled) {
            console.error(`[${source}][SYSTEM]`, ...args);
        }
    }
}

/**
 * Event queue manager
 */
class EventQueue {
    constructor(maxSize = 100) {
        this.eventQueue = [];
        this.identifyQueue = [];
        this.userPropertyQueue = [];
        this.userArrayPropertyQueue = [];
        this.maxSize = maxSize;
    }

    addEvent(eventName, eventData) {
        if (this.eventQueue.length >= this.maxSize) {
            Logger.warn('EventQueue', `Queue is full (${this.maxSize}); removing the oldest event`);
            this.eventQueue.shift();
        }

        this.eventQueue.push({
            name: eventName,
            data: eventData,
            timestamp: Date.now()
        });

        Logger.log('EventQueue', `Event added to queue: ${eventName} (queue size: ${this.eventQueue.length})`);
    }

    addIdentify(userId, userProperties) {
        this.identifyQueue.push({
            userId,
            userProperties,
            timestamp: Date.now()
        });
        Logger.log('EventQueue', `User identify added to queue: ${userId}`);
    }

    addUserProperty(propertyName, propertyValue) {
        this.userPropertyQueue.push({
            propertyName,
            propertyValue,
            timestamp: Date.now()
        });
        Logger.log('EventQueue', `User property added to queue: ${propertyName}`);
    }

    addUserArrayProperty(propertyName, propertyValue) {
        this.userArrayPropertyQueue.push({
            propertyName,
            propertyValue,
            timestamp: Date.now()
        });
        Logger.log('EventQueue', `User array property added to queue: ${propertyName}`);
    }

    processEvents(processor) {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            try {
                const eventDataWithQueueFlag = {
                    ...event.data,
                    is_queue: true,
                    original_timestamp: event.timestamp,
                    queue_delay: Date.now() - event.timestamp
                };
                processor.processEvent(event.name, eventDataWithQueueFlag);
                Logger.log('EventQueue', `Queued event processed: ${event.name}`);
            } catch (error) {
                Logger.error('EventQueue', `Failed to process queued event: ${event.name}`, error);
                this.eventQueue.unshift(event); // Put the event back at the front of the queue
                break;
            }
        }
    }

    processIdentifies(processor) {
        while (this.identifyQueue.length > 0) {
            const identify = this.identifyQueue.shift();
            try {
                processor.processIdentify(identify.userId, identify.userProperties);
                Logger.log('EventQueue', `Queued identify processed: ${identify.userId}`);
            } catch (error) {
                Logger.error('EventQueue', `Failed to process queued identify: ${identify.userId}`, error);
                this.identifyQueue.unshift(identify);
                break;
            }
        }
    }

    processUserProperties(processor) {
        while (this.userPropertyQueue.length > 0) {
            const property = this.userPropertyQueue.shift();
            try {
                processor.processUserProperty(property.propertyName, property.propertyValue);
                Logger.log('EventQueue', `Queued user property processed: ${property.propertyName}`);
            } catch (error) {
                Logger.error('EventQueue', `Failed to process queued user property: ${property.propertyName}`, error);
                this.userPropertyQueue.unshift(property);
                break;
            }
        }
    }

    processUserArrayProperties(processor) {
        while (this.userArrayPropertyQueue.length > 0) {
            const property = this.userArrayPropertyQueue.shift();
            try {
                processor.processUserArrayProperty(property.propertyName, property.propertyValue);
                Logger.log('EventQueue', `Queued user array property processed: ${property.propertyName}`);
            } catch (error) {
                Logger.error('EventQueue', `Failed to process queued user array property: ${property.propertyName}`, error);
                this.userArrayPropertyQueue.unshift(property);
                break;
            }
        }
    }

    getStatus() {
        return {
            eventQueueSize: this.eventQueue.length,
            identifyQueueSize: this.identifyQueue.length,
            userPropertyQueueSize: this.userPropertyQueue.length,
            userArrayPropertyQueueSize: this.userArrayPropertyQueue.length
        };
    }

    clear() {
        this.eventQueue = [];
        this.identifyQueue = [];
        this.userPropertyQueue = [];
        this.userArrayPropertyQueue = [];
        Logger.log('EventQueue', 'All queues have been cleared');
    }
}

/**
 * SDK manager
 */
class SDKManager {
    constructor(config) {
        this.config = config;
        this.sdkObject = null;
        this.isReady = false;
        this.checkStartTime = Date.now();
        this.checkEndTime = null;
    }

    async checkSDK() {
        if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
            Logger.log('SDKManager', `Starting SDK check: ${this.config.name}`);
        }

        if (!this.config.objectPath) {
            // No external SDK required
            this.setReady(null);
            return true;
        }

        const sdkObject = this.resolveObjectPath(this.config.objectPath);

        if (this.validateSDK(sdkObject)) {
            this.setReady(sdkObject);
            return true;
        }

        if (this.config.waitForSDK) {
            return this.waitForSDK();
        }

        return false;
    }

    resolveObjectPath(path) {
        try {
            return eval(path);
        } catch (error) {
            if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                Logger.error('SDKManager', `Failed to resolve object path: ${path}`, error);
            }
            return null;
        }
    }

    validateSDK(sdkObject) {
        if (!sdkObject) return false;

        // Validate required methods
        for (const method of this.config.requiredMethods || []) {
            if (typeof sdkObject[method] !== 'function') {
                if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                    Logger.log('SDKManager', `Missing required method: ${method}`);
                }
                return false;
            }
        }

        // Validate required properties
        for (const property of this.config.requiredProperties || []) {
            if (!(property in sdkObject)) {
                if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                    Logger.log('SDKManager', `Missing required property: ${property}`);
                }
                return false;
            }
        }

        // Run additional validation
        if (this.config.additionalValidation) {
            try {
                if (!this.config.additionalValidation(sdkObject)) {
                    if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                        Logger.log('SDKManager', 'Additional validation failed');
                    }
                    return false;
                }
            } catch (error) {
                if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                    Logger.error('SDKManager', 'Additional validation threw an error', error);
                }
                return false;
            }
        }

        return true;
    }

    async waitForSDK() {
        const { maxChecks = 100, checkInterval = 100 } = this.config;

        return new Promise((resolve) => {
            let checkCount = 0;

            const interval = setInterval(() => {
                checkCount++;
                if (DLPixelH && DLPixelH._shouldLogSDK(true)) {
                    Logger.log('SDKManager', `SDK check attempt ${checkCount}/${maxChecks}`);
                }

                const sdkObject = this.resolveObjectPath(this.config.objectPath);

                if (this.validateSDK(sdkObject)) {
                    this.setReady(sdkObject);
                    clearInterval(interval);
                    resolve(true);
                } else if (checkCount >= maxChecks) {
                    if (DLPixelH && DLPixelH._shouldLogSDK(false)) {
                        Logger.error('SDKManager', `SDK check timed out: ${this.config.name}`);
                    }
                    clearInterval(interval);
                    resolve(false);
                }
            }, checkInterval);
        });
    }

    setReady(sdkObject) {
        this.sdkObject = sdkObject;
        this.isReady = true;
        this.checkEndTime = Date.now();

        const duration = this.checkEndTime - this.checkStartTime;
        if (DLPixelH && DLPixelH._shouldLogSDK(false)) {
            Logger.log('SDKManager', `SDK ready: ${this.config.name}, took ${duration}ms`);
        }
    }

    getSDKObject() {
        return this.sdkObject;
    }

    isSDKReady() {
        return this.isReady;
    }

    getCheckDuration() {
        return this.checkEndTime ? this.checkEndTime - this.checkStartTime : null;
    }
}

/**
 * Retry manager
 */
class RetryManager {
    constructor(maxRetries = 3, retryDelay = 1000) {
        this.maxRetries = maxRetries;
        this.retryDelay = retryDelay;
    }

    async retry(operation, context = '') {
        let lastError;

        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error;
                Logger.error('RetryManager', `${context} failed (attempt ${attempt + 1}/${this.maxRetries + 1})`, error);

                if (attempt < this.maxRetries) {
                    Logger.log('RetryManager', `Retrying in ${this.retryDelay}ms...`);
                    await this.delay(this.retryDelay);
                }
            }
        }

        throw lastError;
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// ==================== Base platform layer ====================

/**
 * Base platform class (simplified)
 */
class BasePixel {
    static errorReportingEnabled = false;
    constructor(config) {
        this.config = config;
        this.name = config.name;
        this.queue = new EventQueue();
        this.sdkManager = new SDKManager(config.sdk || {});
        this.retryManager = new RetryManager();
        this.isInitialized = false;

        this.init();
    }

    async init() {
        Logger.log(this.name, 'Initialization started');

        try {
            await this.sdkManager.checkSDK();
            this.isInitialized = true;

            // Process queued operations
            this.processQueues();

            Logger.log(this.name, 'Initialization completed');
        } catch (error) {
            Logger.error(this.name, 'Initialization failed', error);
        }
    }

    processQueues() {
        if (!this.isSDKReady()) return;

        Logger.log(this.name, 'Starting queue processing');

        this.queue.processEvents(this);
        this.queue.processIdentifies(this);
        this.queue.processUserProperties(this);
        this.queue.processUserArrayProperties(this);
    }

    isSDKReady() {
        return this.sdkManager.isSDKReady();
    }
    // Event handling
    async capture(eventName, eventData) {
        if (!DLPixelH.canSendAnalytics()) return;

        Logger.log(this.name, `Processing event: ${eventName}`);

        if (!this.shouldProcessEvent(eventName, eventData)) {
            Logger.log(this.name, `Event filtered out: ${eventName}`);
            return;
        }

        if (Logger.isDebugMode) {
            Logger.log(this.name, `[Debug mode] Event: ${eventName}`, eventData);
            return;
        }

        if (this.isSDKReady()) {
            try {
                await this.retryManager.retry(
                    () => {
                        if (!DLPixelH.canSendAnalytics()) return;
                        return this.sendEvent(eventName, eventData);
                    },
                    `Send event ${eventName}`
                );
            } catch (error) {
                Logger.error(this.name, `Event send ultimately failed: ${eventName}`, error);
                this.reportError('event_send_failed', { eventName, eventData, error: error.message });
            }
        } else {
            this.queue.addEvent(eventName, eventData);
        }
    }

    // User identification
    async identify(userId, userProperties) {
        Logger.log(this.name, `Processing identify: ${userId}`);

        if (Logger.isDebugMode) {
            Logger.log(this.name, `[Debug mode] Identify: ${userId}`, userProperties);
            return;
        }

        if (this.isSDKReady()) {
            try {
                await this.retryManager.retry(
                    () => this.sendIdentify(userId, userProperties),
                    `Identify user ${userId}`
                );
            } catch (error) {
                Logger.error(this.name, `User identify ultimately failed: ${userId}`, error);
                this.reportError('identify_failed', { userId, userProperties, error: error.message });
            }
        } else {
            this.queue.addIdentify(userId, userProperties);
        }
    }

    // Set user properties
    async setUserProperty(propertyName, propertyValue) {
        Logger.log(this.name, `Setting user property: ${propertyName}`);

        if (!this.shouldProcessUserProperty(propertyName, propertyValue)) {
            Logger.log(this.name, `User property filtered out: ${propertyName}`);
            return;
        }

        if (Logger.isDebugMode) {
            Logger.log(this.name, `[Debug mode] User property: ${propertyName}`, propertyValue);
            return;
        }

        if (this.isSDKReady()) {
            try {
                await this.retryManager.retry(
                    () => this.sendUserProperty(propertyName, propertyValue),
                    `Set user property ${propertyName}`
                );
            } catch (error) {
                Logger.error(this.name, `Setting user property ultimately failed: ${propertyName}`, error);
                this.reportError('user_property_failed', { propertyName, propertyValue, error: error.message });
            }
        } else {
            this.queue.addUserProperty(propertyName, propertyValue);
        }
    }

    // Add user array properties
    async addToUserArrayProperty(propertyName, propertyValue) {
        Logger.log(this.name, `Adding user array property: ${propertyName}`);

        if (!this.shouldProcessUserArrayProperty(propertyName, propertyValue)) {
            Logger.log(this.name, `User array property filtered out: ${propertyName}`);
            return;
        }

        if (Logger.isDebugMode) {
            Logger.log(this.name, `[Debug mode] User array property: ${propertyName}`, propertyValue);
            return;
        }

        if (this.isSDKReady()) {
            try {
                await this.retryManager.retry(
                    () => this.sendUserArrayProperty(propertyName, propertyValue),
                    `Add user array property ${propertyName}`
                );
            } catch (error) {
                Logger.error(this.name, `Adding user array property ultimately failed: ${propertyName}`, error);
                this.reportError('user_array_property_failed', { propertyName, propertyValue, error: error.message });
            }
        } else {
            this.queue.addUserArrayProperty(propertyName, propertyValue);
        }
    }

    // Queue processing interfaces
    processEvent(eventName, eventData) {
        if (!DLPixelH.canSendAnalytics()) return;
        return this.sendEvent(eventName, eventData);
    }

    processIdentify(userId, userProperties) {
        return this.sendIdentify(userId, userProperties);
    }

    processUserProperty(propertyName, propertyValue) {
        return this.sendUserProperty(propertyName, propertyValue);
    }

    processUserArrayProperty(propertyName, propertyValue) {
        return this.sendUserArrayProperty(propertyName, propertyValue);
    }

    // Abstract methods that subclasses must implement
    sendEvent(eventName, eventData) {
        throw new Error(`${this.name} must implement the sendEvent method`);
    }

    sendIdentify(userId, userProperties) {
        throw new Error(`${this.name} must implement the sendIdentify method`);
    }

    sendUserProperty(propertyName, propertyValue) {
        throw new Error(`${this.name} must implement the sendUserProperty method`);
    }

    sendUserArrayProperty(propertyName, propertyValue) {
        throw new Error(`${this.name} must implement the sendUserArrayProperty method`);
    }

    // Filter methods that subclasses may override
    shouldProcessEvent(eventName, eventData) {
        return true;
    }

    shouldProcessUserProperty(propertyName, propertyValue) {
        return true;
    }

    shouldProcessUserArrayProperty(propertyName, propertyValue) {
        return true;
    }

    // Error reporting (gated by feature flag)
    reportError(errorType, errorData) {
        if (!BasePixel.errorReportingEnabled) {
            return;
        }
        if (!Logger.isDebugMode && typeof DLPixelH !== 'undefined') {
            try {
                DLPixelH.capture(errorType, {
                    platform: this.name,
                    ...errorData,
                    timestamp: Date.now()
                });
            } catch (error) {
                Logger.error(this.name, 'Failed to report error', error);
            }
        }
    }

    // Get status
    getStatus() {
        return {
            name: this.name,
            isInitialized: this.isInitialized,
            isSDKReady: this.isSDKReady(),
            queueStatus: this.queue.getStatus(),
            checkDuration: this.sdkManager.getCheckDuration()
        };
    }

    // Destroy
    destroy() {
        this.queue.clear();
        Logger.log(this.name, 'Destroyed');
    }
}

// ==================== Concrete platform implementations ====================

/**
 * Braze platform implementation
 */
class BrazePixel extends BasePixel {
    static brazeIdBroadcasted = false;
    static brazeIdRetryCount = 0;
    static maxBrazeIdRetries = 10;

    constructor() {
        super({
            name: 'BrazePixel',
            sdk: {
                name: 'Braze',
                objectPath: 'window.braze',
                requiredMethods: ['logCustomEvent', 'changeUser', 'getUser', 'getDeviceId'],
                requiredProperties: [],
                waitForSDK: true,
                maxChecks: 600,
                checkInterval: 100,
                additionalValidation: (sdkObject) => {
                    try {
                        const user = sdkObject.getUser();
                        return user && typeof user.setCustomUserAttribute === 'function';
                    } catch (error) {
                        return false;
                    }
                }
            }
        });
    }

    async init() {
        await super.init();

        // Broadcast braze_id after the SDK becomes ready
        if (this.isSDKReady() && !BrazePixel.brazeIdBroadcasted) {
            setTimeout(() => this.broadcastBrazeId(), 100);
        }
    }

    sendEvent(eventName, eventData) {
        const sdkObject = this.sdkManager.getSDKObject();
        const brazeEventData = this.convertEventDataForBraze(eventData);

        sdkObject.logCustomEvent(eventName, brazeEventData);

        if (typeof sdkObject.requestImmediateDataFlush === 'function') {
            sdkObject.requestImmediateDataFlush();
        }
    }

    sendIdentify(userId, userProperties) {
        const sdkObject = this.sdkManager.getSDKObject();
        const user = sdkObject.getUser();

        if (!user) {
            throw new Error('Braze user object not available');
        }

        // Set the user's email
        if (typeof user.setEmail === 'function') {
            user.setEmail(userId);
        }

        // Add a user alias
        if (typeof user.addAlias === 'function') {
            user.addAlias(userId, 'user_email');
        }

        // Set email notification subscription
        if (typeof user.setEmailNotificationSubscriptionType === 'function') {
            user.setEmailNotificationSubscriptionType('opted_in');
        }

        // Set the user ID
        if (typeof sdkObject.changeUser === 'function') {
            sdkObject.changeUser(userId);
        }

        // Set user attributes
        if (userProperties && Object.keys(userProperties).length > 0) {
            const brazeUserProperties = this.sanitizeDataForBraze(userProperties);
            if (typeof sdkObject.setUserAttributes === 'function') {
                sdkObject.setUserAttributes(brazeUserProperties);
            }
        }
    }

    sendUserProperty(propertyName, propertyValue) {
        const sdkObject = this.sdkManager.getSDKObject();
        const user = sdkObject.getUser();

        if (!user || typeof user.setCustomUserAttribute !== 'function') {
            throw new Error('Braze setCustomUserAttribute method not available');
        }

        user.setCustomUserAttribute(propertyName, propertyValue);

        if (typeof sdkObject.requestImmediateDataFlush === 'function') {
            sdkObject.requestImmediateDataFlush();
        }
    }

    sendUserArrayProperty(propertyName, propertyValue) {
        const sdkObject = this.sdkManager.getSDKObject();
        const user = sdkObject.getUser();

        if (!user || typeof user.addToCustomAttributeArray !== 'function') {
            throw new Error('Braze addToCustomAttributeArray method not available');
        }

        user.addToCustomAttributeArray(propertyName, propertyValue);

        if (typeof sdkObject.requestImmediateDataFlush === 'function') {
            sdkObject.requestImmediateDataFlush();
        }
    }

    convertEventDataForBraze(eventData) {
        const { is_queue, original_timestamp, queue_delay, ...brazeEventData } = eventData;

        if (!brazeEventData.timestamp) {
            brazeEventData.timestamp = Date.now();
        }

        return this.sanitizeDataForBraze(brazeEventData);
    }

    sanitizeDataForBraze(data) {
        if (!data || typeof data !== 'object') {
            return {};
        }

        const sanitized = {};

        for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'function') continue;

            if (value !== null && value !== undefined) {
                if (typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean' ||
                    Array.isArray(value) ||
                    (typeof value === 'object' && !(value instanceof Date))) {
                    sanitized[key] = value;
                } else if (value instanceof Date) {
                    sanitized[key] = value.getTime();
                } else {
                    sanitized[key] = String(value);
                }
            }
        }

        return sanitized;
    }

    shouldProcessEvent(eventName, eventData) {
        // Explicitly exclude ecommerce events defined by ECommerceEventHelper and sent to Amplitude
        // Only exclude the event names below to avoid filtering unrelated ecommerce events
        const ecommerceEventsToExclude = new Set([
            'page_viewed',
            'product_viewed',
            'collection_viewed',
            'product_added_to_cart_item',
            'product_added_to_cart',
            'product_buy_now',
            'product_removed_from_cart_item',
            'product_removed_from_cart',
            'checkout_started_item',
            'checkout_started',

            // Additional cart tracking events
            'cart_delete_main',
            'cart_delete_minor',
            'cart_main_quantity_minus',
            'cart_main_quantity_plus',
            'cart_main_variant_change',
            'cart_minor_variant_change',
            'cart_checkout_expand',
            'addon_exposure',
            'shopify_installments_click'
        ]);
        if (ecommerceEventsToExclude.has(eventName)) {
            return false;
        }

        const excludedEvents = [
            'operation_failed_after_retries',
            'queue_event_send_failed',
            'queue_identify_send_failed',
            'queue_user_property_send_failed',
            'queue_user_array_property_send_failed',
            'user_property_send_failed_after_retries',
            'user_array_property_send_failed_after_retries',

            // Footer subscribe behavior events are used for Amplitude funnels only and should not enter Braze
            'footer_subscribe'
        ];

        // sdk_check_time is gated by the system error-reporting switch
        if (eventName === 'sdk_check_time' && !BasePixel.errorReportingEnabled) {
            return false;
        }

        return !excludedEvents.includes(eventName);
    }

    broadcastBrazeId() {
        if (BrazePixel.brazeIdBroadcasted) return;

        try {
            const sdkObject = this.sdkManager.getSDKObject();

            if (typeof sdkObject.getDeviceId === 'function') {
                const deviceId = sdkObject.getDeviceId();

                // Validate that deviceId is a real string rather than true or another invalid value
                if (!deviceId || typeof deviceId !== 'string' || deviceId === 'true') {
                    BrazePixel.brazeIdRetryCount++;

                    if (BrazePixel.brazeIdRetryCount <= BrazePixel.maxBrazeIdRetries) {
                        Logger.log(this.name, `Waiting for a valid braze device ID; current value is invalid: ${deviceId}. Retry ${BrazePixel.brazeIdRetryCount}/${BrazePixel.maxBrazeIdRetries}`);
                        // Retry after 1 second
                        setTimeout(() => this.broadcastBrazeId(), 1000);
                    } else {
                        Logger.error(this.name, `Maximum retry count reached (${BrazePixel.maxBrazeIdRetries}); giving up on fetching the braze device ID`);
                    }
                    return;
                }

                // Only mark the broadcast as complete after a valid deviceId is obtained
                BrazePixel.brazeIdBroadcasted = true;
                // Reset the retry counter
                BrazePixel.brazeIdRetryCount = 0;

                Logger.log(this.name, 'Successfully obtained a valid braze device ID:', deviceId);

                if (window.Shopify && window.Shopify.analytics &&
                    typeof window.Shopify.analytics.publish === 'function') {

                    // Check the braze_id stored in localStorage
                    let storedBrazeId = null;
                    try {
                        if (typeof localStorage !== 'undefined') {
                            storedBrazeId = localStorage.getItem('dl_braze_last_broadcast_id');
                        }
                    } catch (e) { /* ignore localStorage errors */ }

                    // Skip broadcasting if the stored ID matches the current ID
                    if (storedBrazeId === deviceId) {
                        Logger.log(this.name, 'Detected the same braze_id; skipping duplicate broadcast');
                        return;
                    }

                    // Try storing the current braze_id in localStorage first
                    let storageSuccess = false;
                    try {
                        if (typeof localStorage !== 'undefined') {
                            localStorage.setItem('dl_braze_last_broadcast_id', deviceId);
                            storageSuccess = true;
                        }
                    } catch (e) {
                        Logger.error(this.name, 'Failed to store braze_id in localStorage', e);
                    }

                    // Only broadcast after successfully persisting the value to localStorage
                    if (storageSuccess) {
                        window.Shopify.analytics.publish('braze_id', { braze_id: deviceId });
                        Logger.log(this.name, 'braze_id event broadcasted');
                    } else {
                        Logger.log(this.name, 'Skipping braze_id broadcast because localStorage persistence failed');
                    }
                }
            }
        } catch (error) {
            Logger.error(this.name, 'Failed to broadcast braze_id', error);
        }
    }
}

/**
 * Amplitude platform implementation
 */
class AmplitudePixel extends BasePixel {
    constructor() {
        super({
            name: 'AmplitudePixel',
            sdk: {
                name: 'Amplitude',
                objectPath: 'window.Shopify.analytics',
                requiredMethods: ['publish'],
                requiredProperties: [],
                waitForSDK: true,
                maxChecks: 100,
                checkInterval: 100
            }
        });
    }

    sendEvent(eventName, eventData) {
        const sdkObject = this.sdkManager.getSDKObject();
        const amplitudeEventData = this.convertEventDataForAmplitude(eventData);
        const prefixedEventName = `[Petlibro] ${eventName}`;

        sdkObject.publish(prefixedEventName, amplitudeEventData);
    }

    sendIdentify(userId, userProperties) {
        const sdkObject = this.sdkManager.getSDKObject();

        // Persist the user ID to localStorage
        if (userId) {
            try {
                localStorage.setItem('amplitude_user_id', userId);
            } catch (error) {
                Logger.error(this.name, 'Failed to persist user ID', error);
            }
        }

        // Publish the user identification event
        const identifyEvent = {
            userId: userId,
            userProperties: userProperties,
            event_type: 'user_identify'
        };

        sdkObject.publish('[Petlibro Identify] user_identify', identifyEvent);
    }

    sendUserProperty(propertyName, propertyValue) {
        const sdkObject = this.sdkManager.getSDKObject();

        const userPropertyEvent = {
            property_name: propertyName,
            property_value: propertyValue,
            event_type: 'user_property_set'
        };

        sdkObject.publish(`[Petlibro User Property] ${propertyName}`, userPropertyEvent);
    }

    sendUserArrayProperty(propertyName, propertyValue) {
        const sdkObject = this.sdkManager.getSDKObject();

        const userArrayPropertyEvent = {
            property_name: propertyName,
            property_value: propertyValue,
            event_type: 'user_array_property_add'
        };

        sdkObject.publish(`[Petlibro User Array Property] ${propertyName}`, userArrayPropertyEvent);
    }

    convertEventDataForAmplitude(eventData) {
        const { is_queue, original_timestamp, queue_delay, ...amplitudeEventData } = eventData;

        if (!amplitudeEventData.timestamp) {
            amplitudeEventData.timestamp = Date.now();
        }

        return this.sanitizeDataForAmplitude(amplitudeEventData);
    }

    sanitizeDataForAmplitude(data) {
        if (!data || typeof data !== 'object') {
            return {};
        }

        const sanitized = {};

        for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'function') continue;

            if (value !== null && value !== undefined) {
                if (typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean' ||
                    Array.isArray(value) ||
                    (typeof value === 'object' && !(value instanceof Date))) {
                    sanitized[key] = value;
                } else if (value instanceof Date) {
                    sanitized[key] = value.getTime();
                } else {
                    sanitized[key] = String(value);
                }
            }
        }

        return sanitized;
    }

    shouldProcessEvent(eventName, eventData) {
        // sdk_check_time is also gated by the system error-reporting switch
        if (eventName === 'sdk_check_time' && !BasePixel.errorReportingEnabled) {
            return false;
        }
        if (eventName === 'sdk_check_time') {
            return true;
        }
        return true;
    }
}

// ==================== Platform registry ====================

/**
 * Platform registry
 */
class PlatformRegistry {
    constructor() {
        this.platforms = new Map();
        this.pendingSDKCheckData = [];
    }

    register(platform) {
        this.platforms.set(platform.name, platform);
        Logger.log('PlatformRegistry', `Platform registered: ${platform.name}`);
    }

    unregister(platformName) {
        if (this.platforms.has(platformName)) {
            const platform = this.platforms.get(platformName);
            platform.destroy();
            this.platforms.delete(platformName);
            Logger.log('PlatformRegistry', `Platform unregistered: ${platformName}`);
        }
    }

    getAllPlatforms() {
        return Array.from(this.platforms.values());
    }

    getPlatform(name) {
        return this.platforms.get(name);
    }

    async executeOnAll(method, ...args) {
        const promises = this.getAllPlatforms().map(async (platform) => {
            try {
                if (typeof platform[method] === 'function') {
                    return await platform[method](...args);
                }
            } catch (error) {
                Logger.error('PlatformRegistry', `Platform ${platform.name} failed to execute ${method}`, error);
            }
        });

        return Promise.allSettled(promises);
    }

    getStatus() {
        return this.getAllPlatforms().map(platform => platform.getStatus());
    }

    addSDKCheckData(data) {
        this.pendingSDKCheckData.push(data);
    }

    triggerSDKCheckTimeEvents() {
        if (this.pendingSDKCheckData.length > 0) {
            Logger.log('PlatformRegistry', `Triggering ${this.pendingSDKCheckData.length} SDK check time events`);

            this.pendingSDKCheckData.forEach(data => {
                // Controlled by the system error-reporting switch
                if (!BasePixel.errorReportingEnabled) {
                    return;
                }
                if (!Logger.isDebugMode && typeof DLPixelH !== 'undefined') {
                    try {
                        DLPixelH.capture('sdk_check_time', data);
                    } catch (error) {
                        Logger.error('PlatformRegistry', 'Failed to trigger SDK check time event', error);
                    }
                }
            });

            this.pendingSDKCheckData = [];
        }
    }
}

// ==================== Main public interface ====================

/**
 * DLPixelH v2 - main public interface
 */
class DLPixelH {
    static registry = new PlatformRegistry();
    static initialized = false;
    static cookieYesReady = false;
    static cookieYesAnalyticsAllowed = false;
    static consentListenersBound = false;

    static updateCookieYesConsent(detail) {
        const categories = detail?.categories;
        const accepted = detail?.accepted;

        if (categories && typeof categories.analytics === 'boolean') {
            DLPixelH.cookieYesReady = true;
            DLPixelH.cookieYesAnalyticsAllowed = categories.analytics;
            return;
        }

        if (Array.isArray(accepted)) {
            DLPixelH.cookieYesReady = true;
            DLPixelH.cookieYesAnalyticsAllowed = accepted.includes('analytics');
        }
    }

    static setupConsentHandling() {
        if (DLPixelH.consentListenersBound || typeof document === 'undefined') return;
        DLPixelH.consentListenersBound = true;

        document.addEventListener('cookieyes_banner_load', (event) => {
            DLPixelH.updateCookieYesConsent(event.detail);
            DLPixelH.init();
        });

        document.addEventListener('cookieyes_consent_update', (event) => {
            DLPixelH.updateCookieYesConsent(event.detail);
            DLPixelH.init();
        });

        // CookieYes syncs its choice into Shopify asynchronously. Retry initialization
        // when Shopify confirms that the visitor consent has been collected.
        document.addEventListener('visitorConsentCollected', () => {
            DLPixelH.init();
        });

        // Support the case where CookieYes finished loading before this script ran.
        if (typeof window.getCkyConsent === 'function') {
            try {
                DLPixelH.updateCookieYesConsent(window.getCkyConsent());
            } catch (error) {
                Logger.error('DLPixelH', 'Failed to read CookieYes consent', error);
            }
        }
    }

    static canSendAnalytics() {
        const privacy = window.Shopify?.customerPrivacy;

        // CookieYes 尚未加载、未允许 analytics，或 Shopify 尚未同步完成时均不发送。
        return DLPixelH.cookieYesReady &&
            DLPixelH.cookieYesAnalyticsAllowed &&
            privacy?.analyticsProcessingAllowed?.() === true;
    }

    static init() {
        DLPixelH.setupConsentHandling();
        if (DLPixelH.initialized) return;
        // Load debug and console mode settings from localStorage first
        Logger.loadFromStorage();

        Logger.systemLog('DLPixelH', 'Starting initialization v2.0');

        // Inspect URL params and apply debug mode
        DLPixelH.checkURLDebugParam();

        // Register platforms
        const brazePixel = new BrazePixel();
        const amplitudePixel = new AmplitudePixel();

        DLPixelH.registry.register(brazePixel);
        DLPixelH.registry.register(amplitudePixel);

        // Trigger SDK check-time events with a slight delay
        setTimeout(() => {
            DLPixelH.registry.triggerSDKCheckTimeEvents();
        }, 100);

        DLPixelH.initialized = true;
        Logger.systemLog('DLPixelH', 'Initialization completed v2.0');

        // Set the store property after initialization completes
        setTimeout(() => {
            let subdomain = window.location.hostname.split('.')[0];
            subdomain = subdomain === 'petlibro' ? 'us' : subdomain;
            DLPixelH.setUserProperty('lastest_shopify_store', subdomain);
        }, 200);

        // Subscribe to cart update events using the delayed subscription mechanism
        this.setupCartEventSubscription();

        // Trigger the page_viewed event immediately without waiting for the DOM
        setTimeout(() => {
            try {
                DLPixelH.ECommerceEventHelper.capturePageViewed();
                Logger.systemLog('DLPixelH', 'Automatically triggered page viewed event');
            } catch (error) {
                Logger.error('DLPixelH', 'Failed to automatically trigger page viewed event:', error);
            }
        }, 300);

        // Page-specific events that require DOM data
        const triggerContentSpecificEvents = () => {
            setTimeout(() => {
                try {
                    // Detect and automatically trigger collection viewed events
                    const currentURL = window.location.pathname;
                    Logger.systemLog('DLPixelH', `Detected current URL: ${currentURL}`);

                    if (currentURL.includes('/collections/') && !currentURL.includes('/products/')) {
                        // On collection pages, trigger collection viewed automatically
                        Logger.systemLog('DLPixelH', 'Collection page detected, preparing to trigger collection viewed event');
                        DLPixelH.ECommerceEventHelper.captureCollectionViewed();
                        Logger.systemLog('DLPixelH', 'Automatically triggered collection viewed event');
                    }

                    // Detect and automatically trigger product viewed events
                    if (currentURL.includes('/products/')) {
                        // On product pages, trigger product viewed automatically
                        Logger.systemLog('DLPixelH', 'Product page detected, preparing to trigger product viewed event');
                        DLPixelH.ECommerceEventHelper.captureProductViewed();
                        Logger.systemLog('DLPixelH', 'Automatically triggered product viewed event');
                    }

                    // Initialize video event listeners
                    DLPixelH.VideoEventHelper.initVideoEventListeners();

                } catch (error) {
                    Logger.error('DLPixelH', 'Failed to automatically trigger content-specific events:', error);
                }
            }, 300);
        };

        // Check whether the DOM is ready for events that depend on page data
        if (document.readyState === 'loading') {
            // The DOM is still loading, so wait for DOMContentLoaded
            document.addEventListener('DOMContentLoaded', triggerContentSpecificEvents);
        } else {
            // The DOM is already ready, so execute immediately
            triggerContentSpecificEvents();
        }
    }
    // Event capture
    static async capture(eventName, eventData = {}) {
        if (!DLPixelH.initialized) DLPixelH.init();
        if (!DLPixelH.canSendAnalytics()) {
            Logger.eventLog('DLPixelH', `Consent blocked event: ${eventName}`);
            return;
        }

        // Automatically enrich base event parameters
        const urlInfo = DLPixelDataHelper.URLHelper.getURLInfo();
        const pageInfo = DLPixelDataHelper.PageHelper.getPageInfo();
        const currency = DLPixelDataHelper.getCurrency();
        const store = DLPixelDataHelper.getStore();

        const enrichedEventData = {
            ...eventData,
            // Base event parameters that all events should include
            store: store,
            currency: currency,
            ...urlInfo, // currentURLInfo, referrerURLInfo, landingURLInfo
            pageInfo: pageInfo
        };

        Logger.eventLog('DLPixelH', `Captured event: ${eventName}`, enrichedEventData);
        await DLPixelH.registry.executeOnAll('capture', eventName, enrichedEventData);
    }

    // User identification
    static async identify(userId, userProperties = {}) {
        if (!DLPixelH.initialized) DLPixelH.init();

        Logger.eventLog('DLPixelH', `Identify user: ${userId}`, userProperties);
        await DLPixelH.registry.executeOnAll('identify', userId, userProperties);
    }

    // Set user properties
    static async setUserProperty(propertyName, propertyValue) {
        if (!DLPixelH.initialized) DLPixelH.init();

        Logger.eventLog('DLPixelH', `Set user property: ${propertyName}`, { [propertyName]: propertyValue });
        await DLPixelH.registry.executeOnAll('setUserProperty', propertyName, propertyValue);
    }

    // Add user array properties
    static async addToUserArrayProperty(propertyName, propertyValue) {
        if (!DLPixelH.initialized) DLPixelH.init();

        Logger.eventLog('DLPixelH', `Add user array property: ${propertyName}`, { [propertyName]: propertyValue });
        await DLPixelH.registry.executeOnAll('addToUserArrayProperty', propertyName, propertyValue);
    }

    // ==================== Ecommerce base event helper ====================

    /**
     * EcommerceEventHelper - internal helper dedicated to ecommerce event handling
     * Covers core ecommerce actions such as page views, product views, cart actions, and checkout
     */
    static ECommerceEventHelper = class ECommerceEventHelper {

        /**
         * Page viewed event
         * @param {Object} pageData - Page data (optional; page info is automatically enriched)
         */
        static capturePageViewed(pageData = {}) {
            return DLPixelH.capture('page_viewed', pageData);
        }

        /**
         * Product viewed event (automatically resolves product and variant info on product pages)
         * @param {Object} productData - Full product data object (optional; auto-resolved on product pages)
         * @param {string|number} variantId - Currently selected variant ID (optional; will be read from the URL when possible)
         */
        static async captureProductViewed(productData = null, variantId = null) {
            Logger.systemLog('ECommerceEventHelper', `Starting captureProductViewed, productData: ${productData ? 'provided' : 'null'}, variantId: ${variantId || 'null'}`);

            let finalProductData = productData;
            let currentVariantId = variantId;

            // If no product data is provided, try to resolve it from the current page
            if (!finalProductData) {
                Logger.systemLog('ECommerceEventHelper', 'No product data provided, attempting to resolve it automatically');
                try {
                    // Extract the product handle from the current URL
                    const currentPath = window.location.pathname;
                    Logger.systemLog('ECommerceEventHelper', `Current path: ${currentPath}`);
                    const productMatch = currentPath.match(/\/products\/([^\/\?]+)/);

                    if (productMatch) {
                        const productHandle = productMatch[1];

                        // Try reading the variant ID from the URL params
                        if (!currentVariantId) {
                            const urlParams = new URLSearchParams(window.location.search);
                            currentVariantId = urlParams.get('variant');
                        }

                        // If still unavailable, try getting the default selected variant from the page
                        if (!currentVariantId) {
                            currentVariantId = this.getDefaultSelectedVariantId();
                        }

                        // Fetch product data; with a variant ID this returns the full structure including variant details
                        finalProductData = await DLPixelDataHelper.ProductHelper.getProductByHandle(productHandle, currentVariantId);
                        Logger.systemLog('ECommerceEventHelper', `Automatically resolved product data: ${productHandle}${currentVariantId ? `, variant: ${currentVariantId}` : ''}`);
                    } else {
                        Logger.warn('ECommerceEventHelper', 'captureProductViewed: not on a product page and no product parameter was provided');
                        return;
                    }
                } catch (error) {
                    Logger.error('ECommerceEventHelper', 'captureProductViewed: failed to resolve product data automatically', error);
                    return;
                }
            }

            if (!finalProductData) {
                Logger.warn('ECommerceEventHelper', 'captureProductViewed: unable to resolve product data');
                return;
            }

            Logger.systemLog('ECommerceEventHelper', 'Product data resolved successfully, preparing to validate and send');

            // If variant data is still missing but productVariants exist, fall back to the first variant
            if (!finalProductData.variant && finalProductData.productVariants && finalProductData.productVariants.length > 0) {
                const defaultVariantId = finalProductData.productVariants[0].variantId;
                if (defaultVariantId) {
                    try {
                        const productHandle = window.location.pathname.match(/\/products\/([^\/\?]+)/)[1];
                        finalProductData = await DLPixelDataHelper.ProductHelper.getProductByHandle(productHandle, defaultVariantId);
                        Logger.systemLog('ECommerceEventHelper', `Using default variant: ${defaultVariantId}`);
                    } catch (error) {
                        Logger.warn('ECommerceEventHelper', 'Failed to resolve default variant data, falling back to base product data', error);
                    }
                }
            }

            // Validate overall data structure integrity
            const validation = DLPixelDataHelper.ProductHelper.validateProductDataStructure(finalProductData);

            if (!validation.isValid) {
                Logger.error('ECommerceEventHelper', 'Product data validation failed:', validation.errors);
                // Still send the event, but record the validation error
            }

            if (validation.warnings.length > 0) {
                Logger.warn('ECommerceEventHelper', 'Product data validation warnings:', validation.warnings);
            }

            // Send the full data structure directly rather than nesting it under a product field
            Logger.systemLog('ECommerceEventHelper', 'Preparing to send the product_viewed event to DLPixelH.capture');
            const result = await DLPixelH.capture('product_viewed', finalProductData);
            Logger.systemLog('ECommerceEventHelper', 'product_viewed event sent');
            return result;
        }

        /**
         * Get the default selected variant ID from the page
         * @returns {string|null} Default variant ID
         */
        static getDefaultSelectedVariantId() {
            try {
                // Prefer the currently selected variant from the DL variant picker
                const variantPicker = document.querySelector('dl-product-variant-picker-h');
                if (variantPicker && typeof variantPicker.getSelectVariant === 'function') {
                    const selectedVariant = variantPicker.getSelectVariant();
                    if (selectedVariant && selectedVariant.id) {
                        Logger.systemLog('ECommerceEventHelper', `Resolved variant ID from the DL variant picker: ${selectedVariant.id}`);
                        return selectedVariant.id.toString();
                    }
                }

                // If no variant is selected, fall back to the first variant in the global dl_product object
                if (window.dl_product && window.dl_product.variants && window.dl_product.variants.length > 0) {
                    const firstVariant = window.dl_product.variants[0];
                    Logger.systemLog('ECommerceEventHelper', `Resolved the first variant ID from dl_product: ${firstVariant.id}`);
                    return firstVariant.id.toString();
                }

                Logger.warn('ECommerceEventHelper', 'Unable to resolve a default variant ID');
                return null;
            } catch (error) {
                Logger.error('ECommerceEventHelper', 'Failed to resolve the default variant ID', error);
                return null;
            }
        }

        /**
         * Collection viewed event (automatically resolves collection info and products on collection pages)
         * @param {Object} collection - Collection object (optional; auto-resolved on collection pages)
         * @param {Array<Object>} products - Array of product objects (optional; auto-resolved from the current page)
         */
        static async captureCollectionViewed(collection = null, products = null) {
            let collectionData = collection;
            let productsData = products;

            // If no collection data is provided, try to resolve the current collection and products automatically
            if (!collectionData) {
                try {
                    // Extract the collection handle from the current URL
                    const currentPath = window.location.pathname;
                    const collectionMatch = currentPath.match(/\/collections\/([^\/\?]+)/);

                    if (collectionMatch) {
                        const collectionHandle = collectionMatch[1];
                        const collectionResponse = await DLPixelDataHelper.ProductHelper.getCollectionByHandle(collectionHandle);
                        Logger.systemLog('ECommerceEventHelper', `Automatically resolved collection data: ${collectionHandle}`);

                        // Validate the returned data structure
                        if (collectionResponse.collection && collectionResponse.products) {
                            // New format: includes collection and products fields
                            collectionData = collectionResponse.collection;
                            if (!productsData) {
                                productsData = collectionResponse.products;
                                Logger.systemLog('ECommerceEventHelper', `Fetched ${productsData.length} products from the API`);
                            }
                        } else if (collectionResponse.collectionId) {
                            // Legacy format: the response itself is the collection object
                            collectionData = collectionResponse;
                        } else {
                            Logger.warn('ECommerceEventHelper', 'Resolved collection data has an invalid format', collectionResponse);
                        }
                    } else {
                        Logger.warn('ECommerceEventHelper', 'captureCollectionViewed: not on a collection page and no collection parameter was provided');
                        return;
                    }
                } catch (error) {
                    Logger.error('ECommerceEventHelper', 'captureCollectionViewed: failed to resolve collection data automatically', error);
                    return;
                }
            }

            if (!collectionData) {
                Logger.warn('ECommerceEventHelper', 'captureCollectionViewed: unable to resolve collection data');
                return;
            }

            // If products are still missing, try reading them from the page DOM
            if (!productsData || productsData.length === 0) {
                try {
                    productsData = await this.getCollectionProducts();
                    Logger.systemLog('ECommerceEventHelper', `Resolved ${productsData.length} collection products from the page DOM`);
                } catch (error) {
                    Logger.warn('ECommerceEventHelper', 'Failed to resolve collection products from the page DOM, using an empty array instead', error);
                    productsData = [];
                }
            }

            // Normalize product data by removing nested structures to match the flattened schema
            const cleanedProducts = this.cleanCollectionProducts(productsData);

            return DLPixelH.capture('collection_viewed', {
                collection: collectionData,
                products: cleanedProducts
            });
        }

        /**
         * Get the list of products on a collection page
         * @returns {Promise<Array>} Product array
         */
        static async getCollectionProducts() {
            try {
                Logger.systemLog('ECommerceEventHelper', 'Starting collection page product retrieval');

                const currentPath = window.location.pathname;
                const collectionMatch = currentPath.match(/\/collections\/([^\/\?]+)/);

                if (!collectionMatch) {
                    Logger.warn('ECommerceEventHelper', 'Not on a collection page');
                    return [];
                }

                const collectionHandle = collectionMatch[1];

                // Prefer JSON data generated by dl-main-collection-h.liquid
                const collectionJsonScript = document.querySelector(`script#pixel-collection-${collectionHandle}`);
                if (collectionJsonScript) {
                    try {
                        const collectionJson = JSON.parse(collectionJsonScript.textContent);
                        if (collectionJson.products && Array.isArray(collectionJson.products)) {
                            Logger.systemLog('ECommerceEventHelper', `Resolved ${collectionJson.products.length} products from page JSON`);
                            return collectionJson.products;
                        }
                    } catch (parseError) {
                        Logger.warn('ECommerceEventHelper', 'Failed to parse page JSON:', parseError);
                    }
                }

                // If no JSON is available on the page, fetch collection.pixel.liquid through AJAX
                Logger.systemLog('ECommerceEventHelper', 'Fetching collection data via AJAX');
                try {
                    const url = `/collections/${collectionHandle}?view=pixel`;
                    const response = await fetch(url);

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }

                    const collectionData = await response.json();
                    if (collectionData.products && Array.isArray(collectionData.products)) {
                        Logger.systemLog('ECommerceEventHelper', `Fetched ${collectionData.products.length} products via AJAX`);
                        return collectionData.products;
                    }
                } catch (ajaxError) {
                    Logger.error('ECommerceEventHelper', 'Failed to fetch collection data via AJAX:', ajaxError);
                }

                Logger.warn('ECommerceEventHelper', 'Unable to resolve collection product data');
                return [];
            } catch (error) {
                Logger.error('ECommerceEventHelper', 'Failed to resolve collection products', error);
                return [];
            }
        }

        /**
         * Normalize collection product data to match the Product definition
         * @param {Array} products - Raw product array
         * @returns {Array} Normalized product array
         */
        static cleanCollectionProducts(products) {
            if (!Array.isArray(products)) return [];

            return products.map(productData => ({
                productId: productData.product.productId || productData.product.id?.toString() || '',
                productTitle: productData.product.productTitle || productData.product.title || '',
                productCategory: productData.product.productCategory || productData.product.type || '',
                productUrl: productData.product.productUrl || productData.product.url || '',
                hasSubscriptionVariant: productData.product.hasSubscriptionVariant || false,
                hasPreOrderVariant: productData.product.hasPreOrderVariant || false
            }));
        }

        /**
         * Validate the integrity of a LineItem structure
         * @param {Object} lineItem - LineItem object
         * @returns {Object} Validation result {isValid: boolean, errors: Array, warnings: Array}
         */
        static validateLineItemStructure(lineItem) {
            const result = {
                isValid: true,
                errors: [],
                warnings: []
            };

            if (!lineItem) {
                result.errors.push('LineItem is required');
                result.isValid = false;
                return result;
            }

            // Validate required Variant fields
            if (!lineItem.variant) {
                result.errors.push('LineItem.variant is required');
                result.isValid = false;
            } else {
                if (!lineItem.variant.variantId) result.errors.push('Variant.variantId is required');
                if (typeof lineItem.variant.variantPrice !== 'number') result.warnings.push('Variant.variantPrice should be a number');
            }

            // Validate required Product fields
            if (!lineItem.product) {
                result.errors.push('LineItem.product is required');
                result.isValid = false;
            } else {
                if (!lineItem.product.productId) result.errors.push('Product.productId is required');
                if (!lineItem.product.productTitle) result.warnings.push('Product.productTitle is empty');
            }

            // Validate quantity and price fields
            if (typeof lineItem.lineItemQuantity !== 'number' || lineItem.lineItemQuantity <= 0) {
                result.errors.push('LineItem.lineItemQuantity must be a positive number');
                result.isValid = false;
            }

            if (typeof lineItem.lineItemPrice !== 'number') result.warnings.push('LineItem.lineItemPrice should be a number');
            if (typeof lineItem.lineItemFinalPrice !== 'number') result.warnings.push('LineItem.lineItemFinalPrice should be a number');

            return result;
        }

        /**
         * Single-item add-to-cart event
         * @param {Object} lineItem - LineItem object
         * @param {string|Object} context - Add-to-cart source or per-item tracking context.
         */
        static captureProductAddedToCartItem(lineItem, context = '') {
            if (!lineItem) {
                Logger.warn('ECommerceEventHelper', 'captureProductAddedToCartItem: the lineItem parameter is required');
                return;
            }

            // Validate the LineItem structure
            const validation = this.validateLineItemStructure(lineItem);
            if (!validation.isValid) {
                Logger.error('ECommerceEventHelper', 'LineItem data validation failed:', validation.errors);
                // Still send the event, but record the validation error
            }
            if (validation.warnings.length > 0) {
                Logger.warn('ECommerceEventHelper', 'LineItem data validation warnings:', validation.warnings);
            }

            // Build event data through CartHelper
            const normalizedContext = typeof context === 'string'
                ? { cartSource: context }
                : (context || {});
            const eventData = DLPixelDataHelper.CartHelper.getProductAddedToCartItemData(lineItem, normalizedContext);

            const result = DLPixelH.capture('product_added_to_cart_item', eventData);
            return result;
        }

        /**
         * Add-to-cart summary event
         * @param {Object} summary - LineItemsSummary object
         * @param {Array} lineItems - Added LineItem array
         */
        static captureProductAddedToCart(summary, lineItems = []) {
            if (!summary) {
                Logger.warn('ECommerceEventHelper', 'captureProductAddedToCart: the summary parameter is required');
                return;
            }

            // Build event data through CartHelper
            const eventData = DLPixelDataHelper.CartHelper.getProductAddedToCartData(summary, lineItems);

            const result = DLPixelH.capture('product_added_to_cart', eventData);
            return result;
        }

        /**
         * Buy-now summary event. The payload matches product_added_to_cart, while
         * lineItems contains only the products added by the current Buy Now action.
         * @param {Array|Object} responseItems - Items returned by /cart/add.js.
         * @param {string} source - Buy-now source.
         */
        static async captureProductBuyNow(responseItems, source = 'product') {
            const items = Array.isArray(responseItems)
                ? responseItems
                : (responseItems ? [responseItems] : []);

            if (items.length === 0) {
                Logger.warn('ECommerceEventHelper', 'captureProductBuyNow: no purchased items provided');
                return;
            }

            const lineItems = [];
            for (const responseItem of items) {
                try {
                    lineItems.push(this.convertResponseItemToLineItem(responseItem, {}));
                } catch (error) {
                    Logger.error('ECommerceEventHelper', 'Failed to build Buy Now line item', error);
                }
            }

            if (lineItems.length === 0) return;

            const totalQuantity = lineItems.reduce((sum, item) => sum + item.lineItemQuantity, 0);
            const totalOriginalPrice = lineItems.reduce(
                (sum, item) => sum + (item.lineItemPrice * item.lineItemQuantity),
                0
            );
            const totalFinalPrice = lineItems.reduce(
                (sum, item) => sum + (item.lineItemFinalPrice * item.lineItemQuantity),
                0
            );
            const summary = {
                totalQuantity,
                totalItem: lineItems.length,
                totalOriginalPrice: this.fixFloatPrecision(totalOriginalPrice),
                totalFinalPrice: this.fixFloatPrecision(totalFinalPrice),
                totalDiscountPrice: this.fixFloatPrecision(totalOriginalPrice - totalFinalPrice)
            };
            const eventData = {
                ...DLPixelDataHelper.CartHelper.getProductAddedToCartData(summary, lineItems),
                source
            };

            return DLPixelH.capture('product_buy_now', eventData);
        }

        /**
         * Single-item removed-from-cart event
         * @param {Object} lineItem - LineItem object
         * @param {string} cartSource - Removal source (e.g. 'cart_drawer', 'cart_page')
         */
        static captureProductRemovedFromCartItem(lineItem, cartSource = '') {
            if (!lineItem) {
                Logger.warn('ECommerceEventHelper', 'captureProductRemovedFromCartItem: the lineItem parameter is required');
                return;
            }

            return DLPixelH.capture('product_removed_from_cart_item', {
                lineItem: lineItem,
                cartSource: cartSource
            });
        }

        /**
         * Removed-from-cart summary event
         * @param {Object} summary - LineItemsSummary object
         */
        static captureProductRemovedFromCart(summary) {
            if (!summary) {
                Logger.warn('ECommerceEventHelper', 'captureProductRemovedFromCart: the summary parameter is required');
                return;
            }

            return DLPixelH.capture('product_removed_from_cart', {
                summary: summary
            });
        }

        /**
         * Single-item checkout-started event
         * @param {Object} lineItem - LineItem object
         */
        static captureCheckoutStartedItem(lineItem) {
            if (!lineItem) {
                Logger.warn('ECommerceEventHelper', 'captureCheckoutStartedItem: the lineItem parameter is required');
                return;
            }

            return DLPixelH.capture('checkout_started_item', {
                lineItem: lineItem
            });
        }

        /**
         * Checkout-started summary event (automatically resolves the current cart state)
         * @param {Object} summary - LineItemsSummary object (optional; auto-resolved from the current cart)
         */
        static async captureCheckoutStarted(summary = null) {
            let cartSummary = summary;

            // If cart data is not provided, try to resolve it from the current cart automatically
            if (!cartSummary) {
                try {
                    cartSummary = await DLPixelDataHelper.ProductHelper.getCartSummary();
                    Logger.log('ECommerceEventHelper', 'Automatically resolving cart data for the checkout started event');
                } catch (error) {
                    Logger.error('ECommerceEventHelper', 'captureCheckoutStarted: failed to resolve cart data automatically', error);
                    return;
                }
            }

            if (!cartSummary) {
                Logger.warn('ECommerceEventHelper', 'captureCheckoutStarted: unable to resolve cart data');
                return;
            }

            return DLPixelH.capture('checkout_started', {
                summary: cartSummary
            });
        }

        // ==================== Convenience composition methods ====================

        /**
         * Fix floating-point precision issues
         * @param {number} num - Number to normalize
         * @param {number} decimals - Number of decimal places to keep, default is 2
         * @returns {number} Precision-normalized number
         */
        static fixFloatPrecision(num, decimals = 2) {
            return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
        }

        /**
         * Batch add-to-cart event handling (hybrid approach: response data + cart cache)
         * @param {Array} responseItems - Array of items from the add-to-cart response
         * @param {string} cartSource - Add-to-cart source
         */
        static async captureBatchAddToCartWithResponse(responseItems, cartSource = 'product-form') {

            if (!responseItems || !Array.isArray(responseItems) || responseItems.length === 0) {
                Logger.warn('ECommerceEventHelper', 'captureBatchAddToCartWithResponse: invalid response item array');
                return;
            }

            try {
                Logger.systemLog('ECommerceEventHelper', `Processing batched add-to-cart events from response data: ${responseItems.length} items`);

                // 1. Read the pre-add cart state to distinguish new items from quantity increases
                let existingCartItems = {};
                try {
                    // const currentCartData = await DLPixelDataHelper.ProductHelper.getCartData();
                    const currentCartData = null; // prevent fetch cart data for 429 errors
                    if (currentCartData && currentCartData.lineItems) {
                        // Build a map of existing cart items
                        currentCartData.lineItems.forEach(lineItem => {
                            const variantId = lineItem.variant.variantId;
                            const hasSubscription = lineItem.variantSubscription !== null;
                            const key = hasSubscription ? `${variantId}_subscription` : variantId;

                            existingCartItems[key] = {
                                quantity: lineItem.lineItemQuantity,
                                hasSubscription: hasSubscription,
                                subscriptionPlanId: hasSubscription ? lineItem.variantSubscription.planId : null
                            };
                        });
                        Logger.systemLog('ECommerceEventHelper', `Resolved pre-add cart state: ${Object.keys(existingCartItems).length} line items`);
                    }
                } catch (error) {
                    Logger.warn('ECommerceEventHelper', 'Failed to resolve cart state; all items will be treated as new', error);
                }

                // 2. Process every item in the response
                const lineItems = [];
                let totalQuantity = 0;
                let totalOriginalPrice = 0;
                let totalFinalPrice = 0;

                for (const responseItem of responseItems) {
                    try {

                        // Build the LineItem directly from response data
                        const lineItem = this.convertResponseItemToLineItem(responseItem, existingCartItems);

                        lineItems.push(lineItem);

                        // Accumulate summary values with precision normalization
                        totalQuantity += lineItem.lineItemQuantity;
                        const itemTotalPrice = this.fixFloatPrecision(lineItem.lineItemPrice * lineItem.lineItemQuantity);
                        totalOriginalPrice = this.fixFloatPrecision(totalOriginalPrice + itemTotalPrice);
                        const itemFinalPrice = this.fixFloatPrecision(lineItem.lineItemFinalPrice * lineItem.lineItemQuantity);
                        totalFinalPrice = this.fixFloatPrecision(totalFinalPrice + itemFinalPrice);

                        // Trigger the per-item add-to-cart event
                        this.captureProductAddedToCartItem(lineItem, cartSource);

                        const statusText = lineItem.isNewItem ? 'new item' : 'quantity increased';
                        const previousText = lineItem.isQuantityIncrease ? ` (previously ${lineItem.previousQuantity})` : '';

                    } catch (error) {
                        Logger.error('ECommerceEventHelper', `Failed to process response item: ${responseItem.variant_id}`, error);
                    }
                }

                // 3. Build the summary payload and trigger the summary event
                if (lineItems.length > 0) {
                    const newItems = lineItems.filter(item => item.isNewItem);
                    const quantityIncreaseItems = lineItems.filter(item => item.isQuantityIncrease);

                    const summary = {
                        totalQuantity: totalQuantity,
                        totalItem: lineItems.length,
                        totalOriginalPrice: this.fixFloatPrecision(totalOriginalPrice),
                        totalFinalPrice: this.fixFloatPrecision(totalFinalPrice),
                        totalDiscountPrice: this.fixFloatPrecision(totalOriginalPrice - totalFinalPrice),
                        newItemsCount: newItems.length,
                        quantityIncreaseItemsCount: quantityIncreaseItems.length,
                        newItemsQuantity: newItems.reduce((sum, item) => sum + item.lineItemQuantity, 0),
                        quantityIncreaseAmount: quantityIncreaseItems.reduce((sum, item) => sum + item.lineItemQuantity, 0)
                    };

                    // Trigger the aggregated add-to-cart event
                    this.captureProductAddedToCart(summary, lineItems);

                    Logger.systemLog('ECommerceEventHelper', `Aggregated add-to-cart summary event triggered: ${lineItems.length} line items, total quantity: ${totalQuantity} (new items: ${newItems.length}, quantity increased: ${quantityIncreaseItems.length})`);
                }

            } catch (error) {
                Logger.error('ECommerceEventHelper', 'Failed to process batched add-to-cart events', error);
            }
        }

        /**
         * Track one successful add-to-cart operation.
         *
         * Item events describe only the logical items added by the operation, while
         * the cart event describes the complete cart after the operation. Callers
         * should pass itemContexts in the same order as responseItems.
         *
         * @param {Object} options - Successful operation tracking options.
         * @param {Array|Object} options.responseItems - Logical items added by this operation.
         * @param {Array} options.itemContexts - Per-item source and optional parent metadata.
         * @param {string} options.operationId - Stable ID used to suppress duplicate reporting.
         */
        static async captureSuccessfulAddToCart({ responseItems, itemContexts = [], operationId = '' } = {}) {
            const items = Array.isArray(responseItems)
                ? responseItems
                : (responseItems ? [responseItems] : []);

            if (items.length === 0) {
                Logger.warn('ECommerceEventHelper', 'captureSuccessfulAddToCart: no added items provided');
                return;
            }

            if (!this._successfulAddOperationIds) {
                this._successfulAddOperationIds = new Set();
            }

            if (operationId && this._successfulAddOperationIds.has(operationId)) {
                Logger.systemLog('ECommerceEventHelper', `Skipping duplicate add-to-cart operation: ${operationId}`);
                return;
            }

            if (operationId) {
                this._successfulAddOperationIds.add(operationId);
                // Keep the in-memory guard bounded for long-lived sessions.
                if (this._successfulAddOperationIds.size > 100) {
                    const oldestOperationId = this._successfulAddOperationIds.values().next().value;
                    this._successfulAddOperationIds.delete(oldestOperationId);
                }
            }

            const addedLineItems = [];

            for (let index = 0; index < items.length; index++) {
                const responseItem = items[index];
                try {
                    const lineItem = this.convertResponseItemToLineItem(responseItem, {});
                    const context = itemContexts[index] || {};
                    addedLineItems.push(lineItem);
                    await this.captureProductAddedToCartItem(lineItem, context);
                } catch (error) {
                    Logger.error('ECommerceEventHelper', 'Failed to build added item event', error);
                }
            }

            if (addedLineItems.length === 0) return;

            // Cart sections are refreshed before this method is called. Clear the
            // old normalized cache so getCartSummaryData reads the new full state.
            DLPixelDataHelper.ProductHelper.clearCartCache();
            const fullCartData = await DLPixelDataHelper.CartHelper.getCartSummaryData();

            if (fullCartData) {
                await DLPixelH.capture('product_added_to_cart', fullCartData);
                return;
            }

            // Preserve the Cart event if a page has no cart-data mount point and
            // the network fallback fails. This fallback is operation-scoped.
            const totalQuantity = addedLineItems.reduce((sum, item) => sum + item.lineItemQuantity, 0);
            const totalOriginalPrice = addedLineItems.reduce(
                (sum, item) => sum + (item.lineItemPrice * item.lineItemQuantity),
                0
            );
            const totalFinalPrice = addedLineItems.reduce(
                (sum, item) => sum + (item.lineItemFinalPrice * item.lineItemQuantity),
                0
            );
            this.captureProductAddedToCart({
                totalQuantity,
                totalItem: addedLineItems.length,
                totalOriginalPrice: this.fixFloatPrecision(totalOriginalPrice),
                totalFinalPrice: this.fixFloatPrecision(totalFinalPrice),
                totalDiscountPrice: this.fixFloatPrecision(totalOriginalPrice - totalFinalPrice)
            }, addedLineItems);
        }

        /**
         * Extract the handle from a product URL.
         * @param {string} productUrl - Product URL, e.g. "/products/dockstream-2-smart-fountain"
         * @returns {string|null} The extracted handle.
         */
        static extractHandleFromUrl(productUrl) {
            if (!productUrl || typeof productUrl !== 'string') {
                return null;
            }

            // Match the /products/{handle} pattern
            const match = productUrl.match(/\/products\/([^\/\?#]+)/);
            return match ? match[1] : null;
        }

        /**
         * Convert product data from the response into the LineItem shape.
         * @param {Object} responseItem - Product object from the response payload.
         * @param {Object} existingCartItems - Existing cart item map.
         * @returns {Object} A LineItem object.
         */
        static convertResponseItemToLineItem(responseItem, existingCartItems = {}) {

            // Determine whether this item is a subscription product
            const hasSubscription = !!responseItem.selling_plan_allocation;
            const variantId = responseItem.variant_id.toString();
            const cartKey = hasSubscription ? `${variantId}_subscription` : variantId;

            // Determine the item state: new item vs quantity increase
            const existingItem = existingCartItems[cartKey];
            const isNewItem = !existingItem;
            const isQuantityIncrease = existingItem && existingItem.quantity > 0;

            // Build the Variant object
            const variant = {
                variantId: variantId,
                variantTitle: responseItem.variant_title || responseItem.title,
                variantPrice: responseItem.price / 100, // Convert cents into the display currency amount
                variantSKU: responseItem.sku || '',
                variantCompareAtPrice: responseItem.compare_at_price ? responseItem.compare_at_price / 100 : null,
                variantPreOrderInfo: ''
            };

            // Build the Product object
            // Prefer the handle from the response, otherwise extract it from the URL
            const productHandle = responseItem.handle || this.extractHandleFromUrl(responseItem.url);

            // Build a clean product URL without query parameters
            const cleanProductUrl = productHandle ? `/products/${productHandle}` : (responseItem.url || '');

            const product = {
                productId: responseItem.product_id.toString(),
                productTitle: responseItem.product_title,
                productCategory: responseItem.product_type || '',
                productUrl: cleanProductUrl,
                productHandle: productHandle, // Keep the handle for downstream usage
                hasSubscriptionVariant: hasSubscription,
                hasPreOrderVariant: false
            };

            // Build the Subscription object when applicable
            let variantSubscription = null;
            if (hasSubscription && responseItem.selling_plan_allocation) {
                const sellingPlan = responseItem.selling_plan_allocation.selling_plan;
                variantSubscription = {
                    planId: sellingPlan.id.toString(),
                    planName: sellingPlan.name,
                    planPriceAjustmentType: 'percentage',
                    planPriceAjustmentValue: 0,
                    variantPlanPrice: responseItem.selling_plan_allocation.price / 100
                };

                // Try to read discount data from price_adjustments
                if (sellingPlan.price_adjustments && sellingPlan.price_adjustments.length > 0) {
                    const adjustment = sellingPlan.price_adjustments[0];
                    variantSubscription.planPriceAjustmentType = adjustment.value_type;
                    variantSubscription.planPriceAjustmentValue = adjustment.value;
                }
            }

            // Build the Discount object when line-level discounts exist
            let lineItemDiscount = null;
            if (responseItem.line_level_discount_allocations && responseItem.line_level_discount_allocations.length > 0) {
                const discountAllocation = responseItem.line_level_discount_allocations[0];
                const discountApp = discountAllocation.discount_application;

                // Determine discountApplicationValueType
                let discountApplicationValueType = 'percentage';
                if (discountApp.value_type) {
                    discountApplicationValueType = discountApp.value_type;
                } else if (discountApp.value) {
                    // Infer the type from the value structure
                    if (typeof discountApp.value === 'object') {
                        if (discountApp.value.percentage !== undefined) {
                            discountApplicationValueType = 'percentage';
                        } else if (discountApp.value.fixedAmount || discountApp.value.fixed_amount) {
                            discountApplicationValueType = 'fixed_amount';
                        }
                    } else if (discountApp.target_type === 'line_item' && discountApp.value < 1) {
                        // Values below 1 are typically percentages, e.g. 0.1 means 10%
                        discountApplicationValueType = 'percentage';
                    }
                }

                lineItemDiscount = {
                    discountTitle: discountApp.title,
                    discountType: discountApp.type || 'automatic',
                    discountValue: discountAllocation.amount / 100,
                    discountApplicationType: discountApp.target_type,
                    discountApplicationValueType: discountApplicationValueType,
                    discountApplicationValue: discountApp.value,
                    discountTargetSelection: discountApp.target_selection
                };
            }

            return {
                variant: variant,
                variantSubscription: variantSubscription,
                product: product,
                lineItemDiscount: lineItemDiscount,
                lineItemQuantity: responseItem.quantity,
                lineItemPrice: responseItem.original_price / 100,
                lineItemFinalPrice: responseItem.final_price / 100,
                // State flags
                isNewItem: isNewItem,
                isQuantityIncrease: isQuantityIncrease,
                previousQuantity: existingItem ? existingItem.quantity : 0
            };
        }

    };

    /**
     * VideoEventHelper - handles video playback related events.
     * Similar to GA4 enhanced YouTube video tracking.
     */
    static VideoEventHelper = class VideoEventHelper {

        // Store watched video elements to avoid duplicate listeners
        static watchedVideos = new Set();

        // Keep a single observer instance when initialization is triggered more than once
        static videoMutationObserver = null;

        /**
         * Initialize video event listeners.
         */
        static initVideoEventListeners() {
            try {
                Logger.systemLog('VideoEventHelper', 'Initializing video event listeners');

                // Templates do not expose their video elements through document.querySelectorAll.
                // Observe first so videos cloned from a template later can still be tracked.
                this.setupVideoMutationObserver();

                // Find all video elements on the page
                const videos = document.querySelectorAll('video');
                Logger.systemLog('VideoEventHelper', `Found ${videos.length} video elements`);

                if (videos.length === 0) {
                    Logger.warn('VideoEventHelper', 'No video elements found on page');
                    return;
                }

                videos.forEach((video, index) => {
                    this.setupVideoEventListeners(video, index);
                });

                Logger.systemLog('VideoEventHelper', 'Video event listeners initialization completed');
            } catch (error) {
                Logger.error('VideoEventHelper', 'Failed to initialize video event listeners:', error);
            }
        }

        /**
         * Set up event listeners for a single video element.
         * @param {HTMLVideoElement} video - Video element.
         * @param {number} index - Video index.
         */
        static setupVideoEventListeners(video, index) {
            const videoId = this.getVideoId(video, index);

            // Avoid attaching duplicate listeners
            if (this.watchedVideos.has(videoId)) {
                return;
            }

            this.watchedVideos.add(videoId);

            // Skip all event listeners for autoplay videos
            if (video.autoplay || video.classList.contains('dl-autoplay')) {
                return;
            }

            try {
                // Shared completion flag so all listeners can reference the same state
                let hasTriggeredComplete = false;

                // Video ended (non-autoplay videos only)
                video.addEventListener('ended', () => {
                    if (!hasTriggeredComplete) {
                        hasTriggeredComplete = true;
                        this.captureVideoEvent('video_complete', video, videoId);
                    }
                });

                // Add a timeupdate listener as a fallback for completion detection
                video.addEventListener('timeupdate', () => {
                    const isNearEnd = video.duration && video.currentTime >= video.duration - 0.1;

                    if (!hasTriggeredComplete && isNearEnd) {
                        hasTriggeredComplete = true;
                        this.captureVideoEvent('video_complete', video, videoId);
                    }
                });

                // Video started playing (non-autoplay videos only)
                video.addEventListener('play', () => {
                    // Reset the completion flag so replay can emit the complete event again
                    hasTriggeredComplete = false;
                    this.captureVideoEvent('video_start', video, videoId);
                });


                // Video paused (non-autoplay videos only)
                video.addEventListener('pause', () => {
                    // Skip pause reporting if playback completed and caused the pause
                    if (hasTriggeredComplete) {
                        return;
                    }

                    // Check whether playback is near the end, which likely means completion
                    const isNearEnd = video.duration && video.currentTime >= video.duration - 0.1;
                    if (isNearEnd && !hasTriggeredComplete) {
                        hasTriggeredComplete = true;
                        this.captureVideoEvent('video_complete', video, videoId);
                        return;
                    }

                    this.captureVideoEvent('video_pause', video, videoId);
                });

                // Video progress milestones (25%, 50%, 75%, 90%) for non-autoplay videos
                video.addEventListener('timeupdate', () => {
                    this.handleVideoProgress(video, videoId);
                });


            } catch (error) {
                Logger.error('VideoEventHelper', `Failed to setup event listeners for video ${videoId}:`, error);
            }
        }

        /**
         * Handle video progress events.
         * @param {HTMLVideoElement} video - Video element.
         * @param {string} videoId - Video ID.
         */
        static handleVideoProgress(video, videoId) {
            if (!video.duration || video.duration === 0) return;

            const currentTime = video.currentTime;
            const duration = video.duration;
            const progress = (currentTime / duration) * 100;

            // Check whether a milestone has been reached
            const progressPoints = [25, 50, 75, 90];

            for (const point of progressPoints) {
                if (progress >= point && !video[`_progress_${point}`]) {
                    video[`_progress_${point}`] = true;
                    this.captureVideoEvent('video_progress', video, videoId, {
                        progress_percent: point,
                        progress_time: currentTime
                    });
                }
            }
        }

        /**
         * Capture a video event.
         * @param {string} eventName - Event name.
         * @param {HTMLVideoElement} video - Video element.
         * @param {string} videoId - Video ID.
         * @param {Object} additionalData - Additional payload data.
         */
        static captureVideoEvent(eventName, video, videoId, additionalData = {}) {
            try {
                const videoData = {
                    video_id: videoId,
                    video_title: this.getVideoTitle(video),
                    video_duration: video.duration.toFixed(2) || 0,
                    video_current_time: video.currentTime || 0,
                    video_url: video.src || video.currentSrc || '',
                    video_poster: video.poster || '',
                    video_autoplay: video.autoplay || false,
                    video_controls: video.controls || false,
                    video_muted: video.muted || false,
                    video_volume: video.volume || 0,
                    video_playback_rate: video.playbackRate || 1,
                    video_network_state: video.networkState || 0,
                    video_ready_state: video.readyState || 0,
                    video_classname: video.className || '',
                    ...additionalData
                };

                Logger.systemLog('VideoEventHelper', `Capturing video event: ${eventName}`, videoData);

                // Call DLPixelH.capture directly with error handling
                if (typeof DLPixelH !== 'undefined' && typeof DLPixelH.capture === 'function') {
                    DLPixelH.capture(eventName, videoData);
                    Logger.systemLog('VideoEventHelper', `Video event ${eventName} sent to DLPixelH`);
                } else {
                    Logger.error('VideoEventHelper', `DLPixelH.capture not available, failed to send event ${eventName}`);
                }
            } catch (error) {
                Logger.error('VideoEventHelper', `Failed to capture video event: ${eventName}`, error);
            }
        }

        /**
         * Get the video ID.
         * @param {HTMLVideoElement} video - Video element.
         * @param {number} index - Video index.
         * @returns {string} Video ID.
         */
        static getVideoId(video, index) {
            // Prefer the element ID
            if (video.id) {
                return video.id;
            }

            // Use the src as the ID
            if (video.src || video.currentSrc) {
                const src = video.src || video.currentSrc;
                return `video_${src.split('/').pop().split('.')[0]}`;
            }

            // Fall back to the index as the ID
            return `video_${index}`;
        }

        /**
         * Get the video title.
         * @param {HTMLVideoElement} video - Video element.
         * @returns {string} Video title.
         */
        static getVideoTitle(video) {
            // Try the data-title attribute first
            if (video.dataset.title) {
                return video.dataset.title;
            }

            // Try the aria-label attribute
            if (video.getAttribute('aria-label')) {
                return video.getAttribute('aria-label');
            }

            // Try the title attribute
            if (video.title) {
                return video.title;
            }

            // Try to derive the title from the parent element
            const parent = video.closest('[data-video-title], .video-title, .video-title-text');
            if (parent) {
                return parent.textContent?.trim() || parent.dataset.videoTitle || '';
            }

            // Fall back to the source filename as the title
            if (video.src || video.currentSrc) {
                const src = video.src || video.currentSrc;
                return src.split('/').pop().split('.')[0];
            }

            return 'Untitled Video';
        }

        /**
         * Manually test video events for debugging.
         * Run in the browser console: DLPixelH.VideoEventHelper.testVideoEvents()
         */
        static testVideoEvents() {
            try {
                Logger.systemLog('VideoEventHelper', 'Starting manual video event test');

                const videos = document.querySelectorAll('video');
                Logger.systemLog('VideoEventHelper', `Found ${videos.length} video elements`);

                videos.forEach((video, index) => {
                    const videoId = this.getVideoId(video, index);
                    Logger.systemLog('VideoEventHelper', `Testing video ${videoId}:`, {
                        duration: video.duration,
                        currentTime: video.currentTime,
                        readyState: video.readyState,
                        networkState: video.networkState,
                        paused: video.paused,
                        ended: video.ended
                    });

                    // Manually trigger a test event
                    this.captureVideoEvent('video_test', video, videoId, {
                        test_trigger: 'manual',
                        test_time: new Date().toISOString()
                    });
                });

                Logger.systemLog('VideoEventHelper', 'Manual test completed');
            } catch (error) {
                Logger.error('VideoEventHelper', 'Manual test failed:', error);
            }
        }

        /**
         * Set up a MutationObserver for dynamically added videos.
         */
        static setupVideoMutationObserver() {
            try {
                if (this.videoMutationObserver) {
                    return;
                }

                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                // Check whether the newly added node is a video element
                                if (node.tagName === 'VIDEO') {
                                    this.setupVideoEventListeners(node, this.watchedVideos.size);
                                }

                                // Check whether the newly added node contains any videos
                                const videos = node.querySelectorAll ? node.querySelectorAll('video') : [];
                                videos.forEach((video, index) => {
                                    this.setupVideoEventListeners(video, this.watchedVideos.size + index);
                                });
                            }
                        });
                    });
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });

                this.videoMutationObserver = observer;

                Logger.systemLog('VideoEventHelper', 'MutationObserver setup completed');
            } catch (error) {
                Logger.error('VideoEventHelper', 'Failed to setup MutationObserver:', error);
            }
        }
    };

    // ==================== Cart event subscription ====================

    /**
     * Set up the cart event subscription with a reliable retry mechanism.
     * Keep retrying until the subscription succeeds or the max attempts are reached.
     */
    static setupCartEventSubscription() {
        if (this._cartEventSubscriptionInitialized) return;
        this._cartEventSubscriptionInitialized = true;

        let attemptCount = 0;
        const maxAttempts = 50; // Try up to 50 times
        const checkInterval = 200; // Check once every 200ms

        const trySubscribe = () => {
            attemptCount++;

            // Check required objects directly from globals instead of window
            if (typeof subscribe !== 'undefined' &&
                typeof PUB_SUB_EVENTS !== 'undefined' &&
                PUB_SUB_EVENTS.cartUpdate) {

                try {
                    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                        // Only explicit successful add operations are eligible. Quantity
                        // changes, variant rebuilds and page refreshes do not carry tracking.
                        if (event.tracking && event.tracking.addedItems) {
                            Logger.systemLog('DLPixelH', 'Received cart update event, triggering add-to-cart tracking');
                            DLPixelH.ECommerceEventHelper.captureSuccessfulAddToCart({
                                responseItems: event.tracking.addedItems,
                                itemContexts: event.tracking.itemContexts || [],
                                operationId: event.tracking.operationId || ''
                            });
                        }
                    });

                    Logger.systemLog('DLPixelH', `Cart event subscription succeeded (attempt ${attemptCount})`);
                    return true; // Subscription succeeded
                } catch (error) {
                    Logger.error('DLPixelH', `Cart event subscription failed (attempt ${attemptCount}):`, error);
                }
            }

            // Keep retrying if subscription has not succeeded and attempts remain
            if (attemptCount < maxAttempts) {
                setTimeout(trySubscribe, checkInterval);
            } else {
                Logger.warn('DLPixelH', `Cart event subscription failed after reaching the max attempts (${maxAttempts})`);
                Logger.warn('DLPixelH', 'Cart add-to-cart events cannot be captured automatically; please verify that the Shopify theme supports PUB_SUB_EVENTS');
            }

            return false;
        };

        // Try the first subscription attempt immediately
        trySubscribe();
    }

    // ==================== Debug and management methods ====================

    /**
     * Set debug mode: log only without reporting, and force-enable console logs.
     * @param {boolean} enabled - Whether to enable debug mode.
     */
    static setDebugMode(enabled) {
        Logger.setDebugMode(enabled);
        Logger.systemLog('DLPixelH', `Debug mode: ${enabled ? 'enabled (log only, no reporting)' : 'disabled'}`);
    }

    /**
     * Set console mode: control log output independently of debug mode.
     * @param {boolean} enabled - Whether to enable console logs.
     */
    static setConsoleMode(enabled) {
        Logger.setConsoleEnabled(enabled);
        Logger.systemLog('DLPixelH', `Console mode: ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Set event log mode: only controls event-related logging.
     * @param {boolean} enabled - Whether to enable event logs.
     */
    static setEventLogsMode(enabled) {
        Logger.setEventLogsEnabled(enabled);
        Logger.systemLog('DLPixelH', `Event log mode: ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Set system log mode: only controls system-related logging.
     * @param {boolean} enabled - Whether to enable system logs.
     */
    static setSystemLogsMode(enabled) {
        Logger.setSystemLogsEnabled(enabled);
        Logger.systemLog('DLPixelH', `System log mode: ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Set log mode: alias of setConsoleMode for backward compatibility.
     * @param {boolean} enabled - Whether to enable console logs.
     */
    static setLogMode(enabled) {
        DLPixelH.setConsoleMode(enabled);
    }

    // SDK load and readiness logging control (independent toggle)
    static _sdkVerbose = false; // true: log check details; false: log load results only
    static setSDKVerbose(enabled) {
        DLPixelH._sdkVerbose = !!enabled;
        Logger.systemLog('DLPixelH', `Detailed SDK check logs: ${enabled ? 'enabled' : 'disabled'}`);
    }
    static _shouldLogSDK(isDetail) {
        // Requires debug mode or system logging; detailed logs also require _sdkVerbose=true
        const shouldLog = Logger.isDebugMode || Logger.systemLogsEnabled || Logger.isConsoleEnabled;
        if (!shouldLog) return false;
        if (isDetail) return DLPixelH._sdkVerbose === true;
        return true;
    }

    // System error reporting toggle (disabled by default)
    static setErrorReporting(enabled) {
        try {
            BasePixel.errorReportingEnabled = !!enabled;
            Logger.log('DLPixelH', `System error reporting: ${enabled ? 'enabled' : 'disabled'}`);
        } catch (error) {
            // Ignore
        }
    }

    static isDebugMode() {
        return Logger.isDebugMode;
    }

    static isConsoleMode() {
        return Logger.isConsoleEnabled;
    }

    static isEventLogsMode() {
        return Logger.eventLogsEnabled;
    }

    static isSystemLogsMode() {
        return Logger.systemLogsEnabled;
    }

    /**
     * Alias of isConsoleMode, kept for backward compatibility.
     */
    static isLogMode() {
        return DLPixelH.isConsoleMode();
    }

    /**
     * Clear all persisted debug settings.
     */
    static clearStoredSettings() {
        Logger.clearStorage();
        Logger.systemLog('DLPixelH', 'Cleared persisted debug settings');
    }

    /**
     * Get storage status information.
     */
    static getStorageInfo() {
        const storageStatus = Logger.getStorageStatus();
        const currentStatus = {
            current: {
                debugMode: Logger.isDebugMode,
                consoleMode: Logger.isConsoleEnabled,
                eventLogsMode: Logger.eventLogsEnabled,
                systemLogsMode: Logger.systemLogsEnabled
            },
            stored: storageStatus
        };

        Logger.systemLog('DLPixelH', 'Storage status:', currentStatus);
        return currentStatus;
    }

    static getStatus() {
        return DLPixelH.registry.getStatus();
    }

    static getAllInstances() {
        return DLPixelH.registry.getAllPlatforms();
    }

    static debugInstances() {
        const status = DLPixelH.getStatus();
        console.log('DLPixelH Debug Instances:', status);
        return status;
    }

    static checkURLDebugParam() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const debugParam = urlParams.get('dlpixel_debug');
            const logParam = urlParams.get('dlpixel_log');
            const consoleParam = urlParams.get('dlpixel_console');
            const eventLogsParam = urlParams.get('dlpixel_event_logs');
            const systemLogsParam = urlParams.get('dlpixel_system_logs');
            const sdkVerboseParam = urlParams.get('dlpixel_sdk_verbose');

            let changed = false;

            // dlpixel_debug: true debug mode, log only without reporting, and force-enable logging
            if (debugParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_debug=true, enabling debug mode (log only, no reporting)');
                DLPixelH.setDebugMode(true);
                changed = true;
            } else if (debugParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_debug=false, disabling debug mode');
                DLPixelH.setDebugMode(false);
                changed = true;
            }

            // dlpixel_console: explicitly controls console logging, independent of debug mode
            if (consoleParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_console=true, enabling console mode');
                DLPixelH.setConsoleMode(true);
                changed = true;
            } else if (consoleParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_console=false, disabling console mode');
                DLPixelH.setConsoleMode(false);
                changed = true;
            }

            // dlpixel_log: backward-compatible parameter for log output, independent of debug mode
            if (logParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_log=true, enabling console mode (legacy param)');
                DLPixelH.setConsoleMode(true);
                changed = true;
            } else if (logParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_log=false, disabling console mode (legacy param)');
                DLPixelH.setConsoleMode(false);
                changed = true;
            } else if (logParam === 'sdk' || logParam === 'sdk_verbose' || logParam === 'verbose') {
                // Semantic shortcut: enable both console logs and detailed SDK checks
                Logger.systemLog('DLPixelH', `Detected URL param dlpixel_log=${logParam}, enabling console logs and detailed SDK check logs`);
                DLPixelH.setConsoleMode(true);
                DLPixelH.setSDKVerbose(true);
                changed = true;
            } else if (logParam === 'event' || logParam === 'events') {
                // Semantic shortcut: enable event logs only
                Logger.systemLog('DLPixelH', `Detected URL param dlpixel_log=${logParam}, enabling event logs`);
                DLPixelH.setEventLogsMode(true);
                changed = true;
            } else if (logParam === 'system') {
                // Semantic shortcut: enable system logs only
                Logger.systemLog('DLPixelH', `Detected URL param dlpixel_log=${logParam}, enabling system logs`);
                DLPixelH.setSystemLogsMode(true);
                changed = true;
            }

            // dlpixel_event_logs: explicitly controls event logging
            if (eventLogsParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_event_logs=true, enabling event logs');
                DLPixelH.setEventLogsMode(true);
                changed = true;
            } else if (eventLogsParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_event_logs=false, disabling event logs');
                DLPixelH.setEventLogsMode(false);
                changed = true;
            }

            // dlpixel_system_logs: explicitly controls system logging
            if (systemLogsParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_system_logs=true, enabling system logs');
                DLPixelH.setSystemLogsMode(true);
                changed = true;
            } else if (systemLogsParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_system_logs=false, disabling system logs');
                DLPixelH.setSystemLogsMode(false);
                changed = true;
            }

            // dlpixel_sdk_verbose: controls detailed SDK check logging
            if (sdkVerboseParam === 'true') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_sdk_verbose=true, enabling detailed SDK check logs');
                DLPixelH.setSDKVerbose(true);
                changed = true;
            } else if (sdkVerboseParam === 'false') {
                Logger.systemLog('DLPixelH', 'Detected URL param dlpixel_sdk_verbose=false, disabling detailed SDK check logs');
                DLPixelH.setSDKVerbose(false);
                changed = true;
            }

            if (changed) return true;
        } catch (error) {
            Logger.error('DLPixelH', 'Failed to inspect URL debug params', error);
        }

        return false;
    }

    // General-purpose SDK ready callback helper
    static whenSDKReady(callback, options = {}) {
        if (!DLPixelH.initialized) DLPixelH.init();

        const {
            sdkName = 'BrazePixel', // Wait for the Braze SDK by default
            timeout = 60000, // Timeout after 60 seconds
            checkInterval = 100 // Check once every 100ms
        } = options;

        return new Promise((resolve, reject) => {
            // Get the requested SDK instance
            const platform = DLPixelH.registry.getPlatform(sdkName);

            if (!platform) {
                const error = new Error(`SDK platform not found: ${sdkName}`);
                Logger.error('DLPixelH', error.message);
                reject(error);
                return;
            }

            // Run the callback immediately if the SDK is already ready
            if (platform.isSDKReady()) {
                Logger.log('DLPixelH', `SDK ${sdkName} is ready, executing callback immediately`);
                try {
                    const result = callback();
                    resolve(result);
                } catch (error) {
                    Logger.error('DLPixelH', 'Callback execution failed', error);
                    reject(error);
                }
                return;
            }

            // SDK is not ready yet, wait for readiness; detailed logs use a separate toggle
            if (DLPixelH._shouldLogSDK(true)) {
                Logger.log('DLPixelH', `Waiting for SDK ${sdkName} to become ready...`);
            }

            let checkCount = 0;
            const maxChecks = Math.floor(timeout / checkInterval);

            const checkInterval_id = setInterval(() => {
                checkCount++;

                if (platform.isSDKReady()) {
                    clearInterval(checkInterval_id);
                    if (DLPixelH._shouldLogSDK(false)) {
                        Logger.log('DLPixelH', `SDK ${sdkName} is ready (checked ${checkCount} times)`);
                    }

                    try {
                        const result = callback();
                        resolve(result);
                    } catch (error) {
                        Logger.error('DLPixelH', 'Callback execution failed', error);
                        reject(error);
                    }
                } else if (checkCount >= maxChecks) {
                    clearInterval(checkInterval_id);
                    const error = new Error(`Timed out waiting for SDK ${sdkName} to become ready (${timeout}ms)`);
                    if (DLPixelH._shouldLogSDK(false)) {
                        Logger.error('DLPixelH', error.message);
                    }
                    reject(error);
                }
            }, checkInterval);
        });
    }

    // Convenience helper: wait for the Braze SDK
    static whenBrazeReady(callback, timeout = 60000) {
        return DLPixelH.whenSDKReady(callback, { sdkName: 'BrazePixel', timeout });
    }

    // Convenience helper: wait for all SDKs
    static whenAllSDKsReady(callback, timeout = 60000) {
        if (!DLPixelH.initialized) DLPixelH.init();

        return new Promise((resolve, reject) => {
            const checkAllReady = () => {
                const platforms = DLPixelH.registry.getAllPlatforms();
                return platforms.every(platform => platform.isSDKReady());
            };

            if (checkAllReady()) {
                Logger.log('DLPixelH', 'All SDKs are ready, executing callback immediately');
                try {
                    const result = callback();
                    resolve(result);
                } catch (error) {
                    Logger.error('DLPixelH', 'Callback execution failed', error);
                    reject(error);
                }
                return;
            }

            Logger.log('DLPixelH', 'Waiting for all SDKs to become ready...');

            let checkCount = 0;
            const maxChecks = Math.floor(timeout / 100);

            const checkInterval = setInterval(() => {
                checkCount++;

                if (checkAllReady()) {
                    clearInterval(checkInterval);
                    Logger.log('DLPixelH', `All SDKs are ready (checked ${checkCount} times)`);

                    try {
                        const result = callback();
                        resolve(result);
                    } catch (error) {
                        Logger.error('DLPixelH', 'Callback execution failed', error);
                        reject(error);
                    }
                } else if (checkCount >= maxChecks) {
                    clearInterval(checkInterval);
                    const error = new Error(`Timed out waiting for all SDKs to become ready (${timeout}ms)`);
                    Logger.error('DLPixelH', error.message);
                    reject(error);
                }
            }, 100);
        });
    }
}

class DLPixelDataHelper {

    /**
     * Get the current store currency code.
     * @returns {string} ISO currency code, e.g. USD, EUR, GBP.
     */
    static getCurrency() {
        try {
            // Read directly from the global Shopify object
            if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
                return Shopify.currency.active;
            }

            // Default to USD
            return 'USD';
        } catch (error) {
            Logger.error('DLPixelDataHelper', 'Error while resolving currency info:', error);
            return 'USD';
        }
    }

    /**
     * Set the currency code for manual override or caching.
     * @param {string} currency - ISO currency code.
     */
    static setCurrency(currency) {
        try {
            if (currency && typeof currency === 'string' && currency.length === 3) {
                localStorage.setItem('dl_pixel_currency', currency.toUpperCase());
                Logger.log('DLPixelDataHelper', `Currency set to: ${currency.toUpperCase()}`);
            } else {
                Logger.warn('DLPixelDataHelper', 'Invalid currency code:', currency);
            }
        } catch (error) {
            Logger.error('DLPixelDataHelper', 'Error while setting currency info:', error);
        }
    }

    /**
     * Get the current store identifier (subdomain).
     * @returns {string} Store identifier, e.g. us, uk, de.
     */
    static getStore() {
        try {
            // Method 1: read from localStorage if it was stored before
            const storedStore = null;
            //localStorage.getItem('dl_pixel_store');
            if (storedStore) {
                return storedStore;
            }

            // Method 2: derive it from the current URL hostname
            if (typeof window !== 'undefined' && window.location && window.location.hostname) {
                let subdomain = window.location.hostname.split('.')[0];
                // Apply the same normalization logic used in init
                subdomain = subdomain === 'petlibro' ? 'us' : subdomain;

                // Cache the result
                localStorage.setItem('dl_pixel_store', subdomain);
                Logger.log('DLPixelDataHelper', `Resolved store identifier: ${subdomain}`);
                return subdomain;
            }

            // Default to us
            Logger.log('DLPixelDataHelper', 'Unable to resolve store info, falling back to default: us');
            return 'us';
        } catch (error) {
            Logger.error('DLPixelDataHelper', 'Error while resolving store info:', error);
            return 'us';
        }
    }

    /**
     * Set the store identifier for manual override or caching.
     * @param {string} store - Store identifier.
     */
    static setStore(store) {
        try {
            if (store && typeof store === 'string') {
                localStorage.setItem('dl_pixel_store', store);
                Logger.log('DLPixelDataHelper', `Store identifier set to: ${store}`);
            }
        } catch (error) {
            Logger.error('DLPixelDataHelper', 'Error while setting store identifier:', error);
        }
    }

    /**
     * URLHelper - URL utility class (inner class of DLPixelDataHelper).
     */
    static URLHelper = class URLHelper {

        /**
         * Parse a URL and return a structured object.
         * @param {string} url - URL to parse.
         * @returns {Object} Structured object containing the URL parts.
         */
        static parseURL(url) {
            try {
                // If the input is not a full URL, try prepending a protocol
                let fullUrl = url;
                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    fullUrl = 'https://' + url;
                }

                const urlObj = new URL(fullUrl);
                const params = new URLSearchParams(urlObj.search);

                // Build the query parameter array while excluding UTM params
                const queryArray = [];
                for (const [key, value] of params.entries()) {
                    // Exclude UTM params
                    if (!key.toLowerCase().startsWith('utm_')) {
                        queryArray.push({
                            param: key,
                            value: value
                        });
                    }
                }

                if (params.get('irclickid')) {
                    const days = 30;
                    const date = new Date();
                    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                    document.cookie = `dl_impact_irclickid=${params.get('irclickid')}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
                }
                
                // Build the return object
                return {
                    url: fullUrl,
                    host: urlObj.hostname,
                    path: urlObj.pathname,
                    query: queryArray,
                    utmSource: params.get('utm_source') || '',
                    utmMedium: params.get('utm_medium') || '',
                    utmCampaign: params.get('utm_campaign') || '',
                    utmContent: params.get('utm_content') || '',
                    utmTerm: params.get('utm_term') || ''
                };
            } catch (error) {
                // Return a minimal object if parsing fails
                return {
                    url: url,
                    host: '',
                    path: '',
                    query: [],
                    utmSource: '',
                    utmMedium: '',
                    utmCampaign: '',
                    utmContent: '',
                    utmTerm: ''
                };
            }
        }

        /**
         * Get the current page URL info.
         * @returns {Object} URL info object for the current page.
         */
        static getCurrentPageInfo() {
            if (typeof window !== 'undefined' && window.location) {
                return this.parseURL(window.location.href);
            }
            return null;
        }

        /**
         * Get the referrer URL info.
         * @returns {Object} URL info object for the referrer page.
         */
        static getReferrerInfo() {
            if (typeof document !== 'undefined' && document.referrer) {
                return this.parseURL(document.referrer);
            }
            return null;
        }

        /**
         * Compare whether two URLs belong to the same domain.
         * @param {string} url1 - First URL.
         * @param {string} url2 - Second URL.
         * @returns {boolean} Whether both URLs belong to the same domain.
         */
        static isSameDomain(url1, url2) {
            try {
                const info1 = this.parseURL(url1);
                const info2 = this.parseURL(url2);
                return info1.host === info2.host;
            } catch (error) {
                return false;
            }
        }

        /**
         * Extract all UTM parameters from a URL.
         * @param {string} url - URL to parse.
         * @returns {Object} UTM parameter object.
         */
        static extractUTMParams(url) {
            const urlInfo = this.parseURL(url);
            return {
                utm_source: urlInfo.utm_source,
                utm_medium: urlInfo.utm_medium,
                utm_campaign: urlInfo.utm_campaign,
                utm_content: urlInfo.utm_content,
                utm_term: urlInfo.utm_term
            };
        }

        /**
         * Check whether a URL contains UTM parameters.
         * @param {string} url - URL to inspect.
         * @returns {boolean} Whether the URL contains UTM parameters.
         */
        static hasUTMParams(url) {
            const utmParams = this.extractUTMParams(url);
            return Object.values(utmParams).some(value => value !== '');
        }

        // ===== Landing page related helpers =====

        /**
         * Check whether the current page is a landing page entered from an external site.
         * @returns {boolean} Whether the current page is treated as a landing page.
         */
        static isLandingPage() {
            if (typeof window === 'undefined' || typeof document === 'undefined') {
                return false;
            }

            const currentHost = window.location.hostname;
            const referrer = document.referrer;

            // Treat visits without a referrer as direct entries, i.e. landing pages
            if (!referrer) {
                return true;
            }

            try {
                const referrerHost = new URL(referrer).hostname;
                // Treat the visit as external if the referrer domain differs from the current domain
                return referrerHost !== currentHost;
            } catch (error) {
                return true; // Default to landing page when referrer parsing fails
            }
        }

        /**
         * Store landing page info in localStorage.
         * @param {Object} pageInfo - Page URL info object.
         */
        static setLandingPageInfo(pageInfo = null) {
            if (typeof localStorage === 'undefined') {
                return;
            }

            try {
                const infoToStore = pageInfo || this.getCurrentPageInfo();
                if (infoToStore) {
                    // Add a timestamp
                    infoToStore.timestamp = Date.now();
                    infoToStore.isLandingPage = true;

                    localStorage.setItem('dl_landing_page_info', JSON.stringify(infoToStore));
                }
            } catch (error) {
                // Swallow the error silently
            }
        }

        /**
         * Read landing page info from localStorage.
         * @returns {Object|null} Landing page info object, or null if it does not exist.
         */
        static getLandingPageInfo() {
            if (typeof localStorage === 'undefined') {
                return null;
            }

            try {
                const storedInfo = localStorage.getItem('dl_landing_page_info');
                if (storedInfo) {
                    const parsedInfo = JSON.parse(storedInfo);
                    // Remove internal metadata and only return URL-related information
                    const { timestamp, isLandingPage, ...urlInfo } = parsedInfo;
                    return urlInfo;
                }
            } catch (error) {
                return null;
            }

            return null;
        }

        /**
         * Read the full landing page payload from localStorage, including internal metadata.
         * @returns {Object|null} Full landing page info object, or null if it does not exist.
         * @private Internal use only.
         */
        static _getLandingPageInfoWithMetadata() {
            if (typeof localStorage === 'undefined') {
                return null;
            }

            try {
                const storedInfo = localStorage.getItem('dl_landing_page_info');
                if (storedInfo) {
                    return JSON.parse(storedInfo);
                }
            } catch (error) {
                return null;
            }

            return null;
        }

        /**
         * Clear the persisted landing page info.
         */
        static clearLandingPageInfo() {
            if (typeof localStorage === 'undefined') {
                return;
            }

            try {
                localStorage.removeItem('dl_landing_page_info');
            } catch (error) {
                // Swallow the error silently
            }
        }

        /**
         * Automatically detect and persist landing page info.
         * Persist only when the current page qualifies as a landing page.
         */
        static autoDetectAndStoreLandingPage() {
            if (this.isLandingPage()) {
                this.setLandingPageInfo();
                return true;
            }
            return false;
        }

        /**
         * Get the age of the landing page info in days.
         * @returns {number} Days since the info was stored, or -1 if it does not exist.
         */
        static getLandingPageAge() {
            const landingInfo = this._getLandingPageInfoWithMetadata();
            if (!landingInfo || !landingInfo.timestamp) {
                return -1;
            }

            const daysDiff = (Date.now() - landingInfo.timestamp) / (1000 * 60 * 60 * 24);
            return Math.floor(daysDiff);
        }

        /**
         * Check whether landing page info has expired, using 30 days by default.
         * @param {number} maxDays - Maximum valid age in days, default is 30.
         * @returns {boolean} Whether the info has expired.
         */
        static isLandingPageInfoExpired(maxDays = 30) {
            const age = this.getLandingPageAge();
            return age === -1 || age > maxDays;
        }

        /**
         * Get the complete URL information payload.
         * @returns {Object} URL info for the current, referrer, and landing pages.
         */
        static getURLInfo() {
            try {
                return {
                    // Current page URL info
                    currentURLInfo: this.getCurrentPageInfo(),
                    // Referrer page URL info
                    referrerURLInfo: this.getReferrerInfo(),
                    // Landing page URL info
                    landingURLInfo: this.getLandingPageInfo()
                };
            } catch (error) {
                return {
                    currentURLInfo: null,
                    referrerURLInfo: null,
                    landingURLInfo: null
                };
            }
        }
    };

    /**
     * PageHelper - page metadata utility class (inner class of DLPixelDataHelper).
     */
    static PageHelper = class PageHelper {

        /**
         * Get the page title.
         * @returns {string} Page title.
         */
        static getTitle() {
            if (typeof document === 'undefined') {
                return '';
            }
            return document.title || '';
        }

        /**
         * Get the SEO title.
         * @returns {string} SEO title.
         */
        static getSEOTitle() {
            if (typeof document === 'undefined') {
                return '';
            }

            // Priority: og:title > twitter:title > title tag
            const ogTitle = document.querySelector('meta[property="og:title"]');
            if (ogTitle && ogTitle.content) {
                return ogTitle.content;
            }

            const twitterTitle = document.querySelector('meta[name="twitter:title"]');
            if (twitterTitle && twitterTitle.content) {
                return twitterTitle.content;
            }

            const titleMeta = document.querySelector('meta[name="title"]');
            if (titleMeta && titleMeta.content) {
                return titleMeta.content;
            }

            // Fall back to the page title when no SEO title is available
            return this.getTitle();
        }

        /**
         * Get the SEO description.
         * @returns {string} SEO description.
         */
        static getSEODescription() {
            if (typeof document === 'undefined') {
                return '';
            }

            // Priority: og:description > twitter:description > description
            const ogDescription = document.querySelector('meta[property="og:description"]');
            if (ogDescription && ogDescription.content) {
                return ogDescription.content;
            }

            const twitterDescription = document.querySelector('meta[name="twitter:description"]');
            if (twitterDescription && twitterDescription.content) {
                return twitterDescription.content;
            }

            const description = document.querySelector('meta[name="description"]');
            if (description && description.content) {
                return description.content;
            }

            return '';
        }

        /**
         * Get the complete page information payload.
         * @returns {Object} Object containing the title, SEO title, and SEO description.
         */
        static getPageInfo() {
            try {
                return {
                    title: this.getTitle(),
                    seo_title: this.getSEOTitle(),
                    seo_description: this.getSEODescription()
                };
            } catch (error) {
                return {
                    title: '',
                    seo_title: '',
                    seo_description: ''
                };
            }
        }
    };

    /**
     * ProductHelper - product data utility class (inner class of DLPixelDataHelper).
     */
    static ProductHelper = class ProductHelper {

        // In-memory cache to reduce DOM lookups and repeated AJAX requests
        static _memoryCache = new Map();
        static _cacheExpiry = 5 * 60 * 1000; // Cache expires after 5 minutes

        /**
         * Read product data from the in-memory cache.
         * @param {string} key - Cache key.
         * @returns {Object|null} Cached data, or null.
         */
        static getFromMemoryCache(key) {
            const cached = this._memoryCache.get(key);
            if (cached && Date.now() - cached.timestamp < this._cacheExpiry) {
                return cached.data;
            }
            // Automatically clear expired cache entries
            if (cached) {
                this._memoryCache.delete(key);
            }
            return null;
        }

        /**
         * Write data into the in-memory cache.
         * @param {string} key - Cache key.
         * @param {Object} data - Data to cache.
         */
        static setMemoryCache(key, data) {
            this._memoryCache.set(key, {
                data: data,
                timestamp: Date.now()
            });

            // Cap cache size to avoid memory leaks
            if (this._memoryCache.size > 50) {
                const firstKey = this._memoryCache.keys().next().value;
                this._memoryCache.delete(firstKey);
            }
        }

        /**
         * Parse an SKU string.
         * Supports formats like: "PL-WF305-41W & PL-FF305-41W*2"
         * @param {string} sku - SKU string to parse.
         * @returns {Object} Parsed SKU object.
         */
        static parseSKU(sku) {
            const originalSKU = (sku || '').trim();

            if (!originalSKU) {
                return {
                    originalSKU: '',
                    skuArray: [],
                    skuArrayWithoutQuantity: []
                };
            }

            try {
                // Split SKU entries by &
                const skuItems = originalSKU.split('&');
                const skuArray = [];
                const skuArrayWithoutQuantity = [];

                skuItems.forEach(item => {
                    const cleanItem = item.trim();
                    if (!cleanItem) return;

                    let skuTitle, skuQuantity;

                    // Check whether a quantity marker (*) exists
                    if (cleanItem.includes('*')) {
                        const parts = cleanItem.split('*');
                        skuTitle = parts[0].trim();
                        skuQuantity = parseInt(parts[1].trim()) || 1;
                    } else {
                        skuTitle = cleanItem;
                        skuQuantity = 1;
                    }

                    skuArray.push({
                        skuTitle: skuTitle,
                        skuQuantity: skuQuantity
                    });

                    skuArrayWithoutQuantity.push(skuTitle);
                });

                return {
                    originalSKU: originalSKU,
                    skuArray: skuArray,
                    skuArrayWithoutQuantity: skuArrayWithoutQuantity
                };

            } catch (error) {
                return {
                    originalSKU: originalSKU,
                    skuArray: [],
                    skuArrayWithoutQuantity: []
                };
            }
        }

        /**
         * Read data from a DOM script tag.
         * @param {string} scriptId - Script tag ID.
         * @returns {Object|null} Parsed data object, or null on failure.
         */
        static getDataFromScript(scriptId) {
            try {
                const scriptElement = document.getElementById(scriptId);
                if (scriptElement && scriptElement.textContent) {
                    return JSON.parse(scriptElement.textContent);
                }
            } catch (error) {
                console.warn(`Failed to parse data from script ${scriptId}:`, error);
            }
            return null;
        }

        /**
         * Save data into a DOM script tag.
         * @param {string} scriptId - Script tag ID.
         * @param {Object} data - Data object to persist.
         */
        static saveDataToScript(scriptId, data) {
            try {
                // Remove the existing script tag first
                const existingScript = document.getElementById(scriptId);
                if (existingScript) {
                    existingScript.remove();
                }

                // Create a new script tag
                const script = document.createElement('script');
                script.type = 'application/json';
                script.id = scriptId;
                script.textContent = JSON.stringify(data);

                // Append it to the document head
                document.head.appendChild(script);
            } catch (error) {
                console.warn(`Failed to save data to script ${scriptId}:`, error);
            }
        }

        /**
         * Check whether a payload already matches the normalized cart structure.
         * @param {Object} cartData - Candidate cart payload.
         * @returns {boolean} True when the payload matches the cart schema.
         */
        static isNormalizedCartData(cartData) {
            return !!(
                cartData &&
                typeof cartData === 'object' &&
                Array.isArray(cartData.lineItems) &&
                cartData.lineItemsSummary &&
                typeof cartData.lineItemsSummary === 'object'
            );
        }

        /**
         * Convert legacy cart item arrays from the global cart mount point into the normalized cart payload.
         * @param {Array} rawCartItems - Raw cart items from `#cart-item-json`.
         * @returns {Object|null} Normalized cart payload, or null when conversion fails.
         */
        static convertLegacyCartItemsToCartData(rawCartItems) {
            if (!Array.isArray(rawCartItems)) {
                return null;
            }

            const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

            const lineItems = rawCartItems
                .map(item => {
                    try {
                        const normalizedLineItem = DLPixelH.ECommerceEventHelper.convertResponseItemToLineItem(item, {});
                        return DLPixelDataHelper.CartHelper.cleanLineItem(normalizedLineItem);
                    } catch (error) {
                        Logger.warn('ProductHelper', 'Failed to normalize a legacy cart item from #cart-item-json', error);
                        return null;
                    }
                })
                .filter(item => item !== null);

            const lineItemsSummary = lineItems.reduce((summary, item) => {
                const quantity = Number(item.lineItemQuantity) || 0;
                const originalPrice = Number(item.lineItemPrice) || 0;
                const finalPrice = Number(item.lineItemFinalPrice) || 0;

                summary.totalQuantity += quantity;
                summary.totalOriginalPrice = roundCurrency(summary.totalOriginalPrice + (originalPrice * quantity));
                summary.totalFinalPrice = roundCurrency(summary.totalFinalPrice + (finalPrice * quantity));
                return summary;
            }, {
                totalQuantity: 0,
                totalItem: lineItems.length,
                totalOriginalPrice: 0,
                totalFinalPrice: 0,
                totalDiscountPrice: 0
            });

            lineItemsSummary.totalDiscountPrice = roundCurrency(
                lineItemsSummary.totalOriginalPrice - lineItemsSummary.totalFinalPrice
            );

            return {
                lineItems: lineItems,
                lineItemsSummary: lineItemsSummary,
                discounts: DLPixelDataHelper.CartHelper.generateDiscounts(lineItems)
            };
        }

        /**
         * Get product data by product handle.
         * Prefer in-memory and DOM script caches before falling back to an AJAX request.
         * @param {string} handle - Product handle identifier.
         * @param {string|number} variantId - Variant ID, optional.
         * @returns {Promise<Object>} Product data object.
         */
        static async getProductByHandle(handle, variantId = null) {
            if (!handle || typeof handle !== 'string') {
                throw new Error('Handle is required and must be a string');
            }

            const cleanHandle = handle.trim();
            const cacheKey = `product-${cleanHandle}${variantId ? `-${variantId}` : ''}`;

            try {
                // 1. Prefer transformed data from the in-memory cache
                const memoryData = this.getFromMemoryCache(cacheKey);
                if (memoryData) {
                    Logger.systemLog('ProductHelper', `Resolved product from memory cache: ${cleanHandle}`);
                    // Ensure cached data does not expose variantsCompleteData
                    if ('variantsCompleteData' in memoryData) {
                        delete memoryData.variantsCompleteData;
                    }
                    return memoryData;
                }

                // 1.1 For a specific variant, prefer preprocessed variant cache entries
                if (variantId) {
                    const preprocessedData = this.getFromMemoryCache(cacheKey);
                    if (preprocessedData && preprocessedData.variant && preprocessedData.variant.variantId === variantId) {
                        Logger.systemLog('ProductHelper', `Resolved variant from preprocessed cache: ${cleanHandle}-${variantId}`);
                        // Ensure cached data does not expose variantsCompleteData
                        if ('variantsCompleteData' in preprocessedData) {
                            delete preprocessedData.variantsCompleteData;
                        }
                        return preprocessedData;
                    }

                    // If preprocessing cache is missing, reuse base cached data and convert it
                    const baseKey = `product-${cleanHandle}`;
                    const baseRawData = this.getFromMemoryCache(baseKey);
                    if (baseRawData) {
                        Logger.systemLog('ProductHelper', `Converting product variant from base cache: ${cleanHandle}-${variantId}`);

                        // Preprocess data for all variants first
                        this.preprocessAndCacheVariantData(baseRawData, cleanHandle);

                        // Try reading the preprocessed data again
                        const preprocessedData = this.getFromMemoryCache(cacheKey);
                        if (preprocessedData) {
                            // Ensure cached data does not expose variantsCompleteData
                            if ('variantsCompleteData' in preprocessedData) {
                                delete preprocessedData.variantsCompleteData;
                            }
                            return preprocessedData;
                        }

                        // Fall back to the conversion path if preprocessing failed
                        const convertedData = this.convertToNewProductStructure(baseRawData, variantId);
                        // Ensure cached data does not expose variantsCompleteData
                        if ('variantsCompleteData' in convertedData) {
                            delete convertedData.variantsCompleteData;
                        }
                        this.setMemoryCache(cacheKey, convertedData);
                        return convertedData;
                    }
                }

                // 2. Read raw data from the DOM script tag
                const scriptId = `pixel-product-${cleanHandle}`;
                const cachedData = this.getDataFromScript(scriptId);
                let rawProductData = null;

                if (cachedData && !cachedData.variantId) {
                    // Confirm this is product-level data, i.e. no variantId field
                    rawProductData = cachedData;
                    Logger.systemLog('ProductHelper', `Resolved product from DOM cache: ${cleanHandle}`);

                    // Debug log: inspect the DOM cache structure
                    Logger.systemLog('ProductHelper', 'DOM cache data structure:', {
                        hasProductVariants: !!rawProductData.productVariants,
                        hasProductOptions: !!rawProductData.productOptions,
                        hasProductCollections: !!rawProductData.productCollections,
                        productVariantsLength: Array.isArray(rawProductData.productVariants) ? rawProductData.productVariants.length : 'not array',
                        productOptionsLength: Array.isArray(rawProductData.productOptions) ? rawProductData.productOptions.length : 'not array',
                        productCollectionsLength: Array.isArray(rawProductData.productCollections) ? rawProductData.productCollections.length : 'not array',
                        dataKeys: Object.keys(rawProductData)
                    });

                    // Save the raw data into the base cache
                    const baseKey = `product-${cleanHandle}`;
                    this.setMemoryCache(baseKey, rawProductData);

                    // If a specific variant is requested, convert and return immediately
                    if (variantId) {
                        const convertedData = this.convertToNewProductStructure(rawProductData, variantId);
                        // Ensure cached data does not expose variantsCompleteData
                        if ('variantsCompleteData' in convertedData) {
                            delete convertedData.variantsCompleteData;
                        }
                        this.setMemoryCache(cacheKey, convertedData);
                        return convertedData;
                    }
                } else {
                    // 3. If the DOM cache is missing, request the data via AJAX
                    Logger.systemLog('ProductHelper', `Fetching product via AJAX: ${cleanHandle}`);
                    const url = `/products/${cleanHandle}?view=pixel`;
                    const response = await fetch(url);

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    rawProductData = await response.json();

                    // Debug log: inspect the raw AJAX response payload
                    Logger.systemLog('ProductHelper', 'Raw AJAX response data:', {
                        hasProductVariants: !!rawProductData.productVariants,
                        hasProductOptions: !!rawProductData.productOptions,
                        hasProductCollections: !!rawProductData.productCollections,
                        productVariantsLength: Array.isArray(rawProductData.productVariants) ? rawProductData.productVariants.length : 'not array',
                        productOptionsLength: Array.isArray(rawProductData.productOptions) ? rawProductData.productOptions.length : 'not array',
                        productCollectionsLength: Array.isArray(rawProductData.productCollections) ? rawProductData.productCollections.length : 'not array',
                        dataKeys: Object.keys(rawProductData),
                        responseUrl: response.url,
                        responseStatus: response.status,
                        responseHeaders: Object.fromEntries(response.headers.entries())
                    });

                    // Specifically inspect whether the product includes variants, options, and collections
                    if (rawProductData.product) {
                        Logger.systemLog('ProductHelper', 'Base product info inspection:', {
                            productId: rawProductData.product.productId || rawProductData.product.id,
                            hasVariantsInProduct: !!rawProductData.product.variants,
                            hasOptionsInProduct: !!rawProductData.product.options,
                            hasCollectionsInProduct: !!rawProductData.product.collections,
                            productKeys: Object.keys(rawProductData.product)
                        });
                    }

                    // Check whether the response is raw Shopify product data instead of dl-pixel-product data
                    // Raw Shopify payloads contain fields like id, handle, and variants rather than our structured format
                    if (rawProductData.id && rawProductData.handle && rawProductData.variants &&
                        !rawProductData.productVariants && !rawProductData.productOptions && !rawProductData.productCollections) {

                        Logger.warn('ProductHelper', 'AJAX returned raw Shopify data; product.pixel.liquid did not execute correctly, falling back to manual structure building');

                        // Manually build the dl-pixel-product structure from the raw Shopify product object
                        const structuredData = this.buildStructuredDataFromShopifyProduct(rawProductData);
                        rawProductData = structuredData;

                        Logger.systemLog('ProductHelper', 'Data structure after manual build:', {
                            hasProductVariants: !!rawProductData.productVariants,
                            hasProductOptions: !!rawProductData.productOptions,
                            hasProductCollections: !!rawProductData.productCollections,
                            productVariantsLength: Array.isArray(rawProductData.productVariants) ? rawProductData.productVariants.length : 'not array',
                            productOptionsLength: Array.isArray(rawProductData.productOptions) ? rawProductData.productOptions.length : 'not array',
                            productCollectionsLength: Array.isArray(rawProductData.productCollections) ? rawProductData.productCollections.length : 'not array'
                        });
                    }

                    // Persist the response into the DOM script tag cache
                    this.saveDataToScript(scriptId, rawProductData);
                }

                // Save raw data into the base cache first so DOM and AJAX cache paths stay consistent
                const baseKey = `product-${cleanHandle}`;
                this.setMemoryCache(baseKey, rawProductData);

                // The Liquid template already preprocessed all variants, so no extra work is needed here

                // Then convert the data shape before returning it
                const convertedData = this.convertToNewProductStructure(rawProductData, variantId);
                // Ensure cached data does not expose variantsCompleteData
                if ('variantsCompleteData' in convertedData) {
                    delete convertedData.variantsCompleteData;
                }
                // Cache the converted payload under the request-specific cache key
                this.setMemoryCache(cacheKey, convertedData);

                return convertedData;
            } catch (error) {
                Logger.error('ProductHelper', `Failed to resolve product: ${cleanHandle}`, error);
                throw error;
            }
        }

        /**
         * Preprocess and cache the full data structure for every variant.
         * This ensures each variant has complete variant, variantOptions, variantSKUs, and variantSubscriptions data.
         * @param {Object} productData - Product data.
         * @param {string} handle - Product handle.
         */
        static preprocessAndCacheVariantData(productData, handle) {
            if (!productData || !productData.productVariants || !Array.isArray(productData.productVariants)) {
                return;
            }

            Logger.systemLog('ProductHelper', `Starting preprocessing for ${productData.productVariants.length} variants of product ${handle}`);

            productData.productVariants.forEach(variant => {
                if (!variant.variantId) return;

                try {
                    // Build the full data structure for the variant
                    const fullVariantData = this.buildCompleteVariantData(variant, productData);

                    // Store it under the variant-specific cache key
                    const variantCacheKey = `product-${handle}-${variant.variantId}`;

                    // Build a product payload that includes the complete variant information
                    const productWithVariant = {
                        ...productData,
                        variant: fullVariantData.variant,
                        variantOptions: fullVariantData.variantOptions,
                        variantSKUs: fullVariantData.variantSKUs,
                        variantSubscriptions: fullVariantData.variantSubscriptions
                    };

                    this.setMemoryCache(variantCacheKey, productWithVariant);

                    Logger.systemLog('ProductHelper', `Cached full data for variant ${variant.variantId}`);
                } catch (error) {
                    Logger.warn('ProductHelper', `Failed to preprocess variant ${variant.variantId}:`, error);
                }
            });
        }

        /**
         * Build the full data structure for a single variant.
         * @param {Object} variant - Variant data.
         * @param {Object} productData - Product data.
         * @returns {Object} Full variant data structure.
         */
        static buildCompleteVariantData(variant, productData) {
            // Build the base variant object
            const variantObj = {
                variantId: variant.variantId,
                variantTitle: variant.variantTitle,
                variantPrice: variant.price || 0,
                variantSKU: this.extractSKUString(variant.sku),
                variantCompareAtPrice: variant.compareAtPrice || null,
                variantPreOrderInfo: variant.preOrderInfo || ''
            };

            // Parse variant subscription data
            const variantSubscriptions = this.extractVariantSubscriptions(variant);

            // Parse the variant SKU array
            const variantSKUs = this.parseVariantSKUs(
                variant.sku,
                variantObj,
                variantSubscriptions.length > 0 ? variantSubscriptions[0] : null
            );

            // Extract variant option data
            const variantOptions = this.extractVariantOptionsFromVariant(
                variant,
                productData.productOptions || []
            );

            return {
                variant: variantObj,
                variantOptions: variantOptions,
                variantSKUs: variantSKUs,
                variantSubscriptions: variantSubscriptions
            };
        }

        /**
         * Manually build the dl-pixel-product structure from raw Shopify product data.
         * Used as a fallback when `product.pixel.liquid` does not execute correctly.
         * @param {Object} shopifyProduct - Raw Shopify product object.
         * @returns {Object} Data in the dl-pixel-product structure.
         */
        static buildStructuredDataFromShopifyProduct(shopifyProduct) {
            if (!shopifyProduct || !shopifyProduct.id) {
                throw new Error('Invalid Shopify product data');
            }

            // Build the base product data
            const product = {
                productId: shopifyProduct.id.toString(),
                productTitle: shopifyProduct.title || '',
                productCategory: shopifyProduct.product_type || '',
                productUrl: shopifyProduct.handle ? `/products/${shopifyProduct.handle}` : '',
                hasSubscriptionVariant: false, // Simplified for now and can be enhanced later
                hasPreOrderVariant: false // Simplified for now and can be enhanced later
            };

            // Build collection data when available
            const productCollections = [];
            if (Array.isArray(shopifyProduct.collections)) {
                shopifyProduct.collections.forEach(collection => {
                    if (collection && collection.id) {
                        productCollections.push({
                            collectionId: collection.id.toString(),
                            collectionTitle: collection.title || '',
                            collectionHandle: collection.handle || ''
                        });
                    }
                });
            }

            // Build option data when available
            const productOptions = [];
            if (Array.isArray(shopifyProduct.options)) {
                shopifyProduct.options.forEach(option => {
                    if (option && option.name && Array.isArray(option.values)) {
                        productOptions.push({
                            optionName: option.name,
                            optionValues: option.values.join('|')
                        });
                    }
                });
            }

            // Build variant data when available
            const productVariants = [];
            if (Array.isArray(shopifyProduct.variants)) {
                shopifyProduct.variants.forEach(variant => {
                    if (variant && variant.id) {
                        productVariants.push({
                            variantId: variant.id.toString(),
                            variantTitle: variant.title || '',
                            price: (variant.price || 0) / 100, // Convert cents into the display currency amount
                            compareAtPrice: variant.compare_at_price ? (variant.compare_at_price / 100) : null,
                            sku: variant.sku || '',
                            preOrderInfo: '', // Simplified handling
                            option1: variant.option1 || null,
                            option2: variant.option2 || null,
                            option3: variant.option3 || null,
                            optionValues: [variant.option1, variant.option2, variant.option3].filter(Boolean),
                            subscriptions: [] // Simplified handling
                        });
                    }
                });
            }

            // Return the dl-pixel-product structure
            return {
                product: product,
                productCollections: productCollections,
                productOptions: productOptions,
                productVariants: productVariants
            };
        }

        /**
         * Validate the integrity of the product data structure.
         * @param {Object} productData - Product data object.
         * @returns {Object} Validation result: {isValid, errors, warnings}.
         */
        static validateProductDataStructure(productData) {
            const result = {
                isValid: true,
                errors: [],
                warnings: []
            };

            if (!productData) {
                result.errors.push('Product data is required');
                result.isValid = false;
                return result;
            }

            // Validate required Product fields
            if (!productData.product) {
                result.errors.push('Product object is missing');
                result.isValid = false;
            } else {
                const product = productData.product;
                if (!product.productId) result.errors.push('Product.productId is required');
                if (!product.productTitle) result.warnings.push('Product.productTitle is empty');
                if (!product.productUrl) result.warnings.push('Product.productUrl is empty');
            }

            // Validate array fields
            if (productData.productVariants && !Array.isArray(productData.productVariants)) {
                result.errors.push('productVariants must be an array');
                result.isValid = false;
            }

            if (productData.productOptions && !Array.isArray(productData.productOptions)) {
                result.errors.push('productOptions must be an array');
                result.isValid = false;
            }

            if (productData.productCollections && !Array.isArray(productData.productCollections)) {
                result.errors.push('productCollections must be an array');
                result.isValid = false;
            }

            // Validate variant-related fields when present
            if (productData.variant) {
                const variant = productData.variant;
                if (!variant.variantId) result.errors.push('Variant.variantId is required');
                if (typeof variant.variantPrice !== 'number') result.warnings.push('Variant.variantPrice should be a number');

                if (productData.variantSKUs && !Array.isArray(productData.variantSKUs)) {
                    result.errors.push('variantSKUs must be an array');
                    result.isValid = false;
                }

                if (productData.variantOptions && !Array.isArray(productData.variantOptions)) {
                    result.errors.push('variantOptions must be an array');
                    result.isValid = false;
                }

                if (productData.variantSubscriptions && !Array.isArray(productData.variantSubscriptions)) {
                    result.errors.push('variantSubscriptions must be an array');
                    result.isValid = false;
                }
            }

            return result;
        }

        /**
         * Convert raw product data into the new reporting structure with cleanup applied.
         * @param {Object} rawProductData - Raw product data.
         * @param {string|number} variantId - Variant ID, optional.
         * @returns {Object} New data structure.
         */
        static convertToNewProductStructure(rawProductData, variantId = null) {
            if (!rawProductData) {
                throw new Error('Raw product data is required');
            }

            // Debug log: inspect the raw data structure
            Logger.systemLog('ProductHelper', 'convertToNewProductStructure raw data fields:', {
                hasProductVariants: !!rawProductData.productVariants,
                hasVariants: !!rawProductData.variants,
                hasProductOptions: !!rawProductData.productOptions,
                hasOptions: !!rawProductData.options,
                hasProductCollections: !!rawProductData.productCollections,
                hasCollections: !!rawProductData.collections,
                hasVariantsCompleteData: !!rawProductData.variantsCompleteData,
                productVariantsLength: Array.isArray(rawProductData.productVariants) ? rawProductData.productVariants.length : 'not array',
                productOptionsLength: Array.isArray(rawProductData.productOptions) ? rawProductData.productOptions.length : 'not array',
                productCollectionsLength: Array.isArray(rawProductData.productCollections) ? rawProductData.productCollections.length : 'not array'
            });

            // Clean the product variant array by removing nested complex structures
            const cleanedVariants = this.cleanProductVariants(rawProductData.productVariants || rawProductData.variants || []);

            // Clean the product option array by flattening option values
            const cleanedOptions = this.cleanProductOptions(rawProductData.productOptions || rawProductData.options || []);

            // Base product structure returned when no variant ID is provided
            const baseStructure = {
                product: rawProductData.product || {
                    productId: rawProductData.productId,
                    productTitle: rawProductData.productTitle,
                    productCategory: rawProductData.productCategory,
                    productUrl: rawProductData.productUrl,
                    hasSubscriptionVariant: rawProductData.hasSubscriptionVariant || false,
                    hasPreOrderVariant: rawProductData.hasPreOrderVariant || false
                },
                productVariants: cleanedVariants,
                productOptions: cleanedOptions,
                productCollections: rawProductData.productCollections || rawProductData.collections || []
            };

            // Debug log: inspect the converted data structure
            Logger.systemLog('ProductHelper', 'convertToNewProductStructure converted data:', {
                productVariantsLength: baseStructure.productVariants.length,
                productOptionsLength: baseStructure.productOptions.length,
                productCollectionsLength: baseStructure.productCollections.length,
                hasProduct: !!baseStructure.product
            });

            // Return the base structure when no variant ID is specified
            if (!variantId) {
                // Remove the internal variantsCompleteData field before returning
                delete baseStructure.variantsCompleteData;
                return baseStructure;
            }

            // Use the prebuilt complete variant data directly when available
            if (rawProductData.variantsCompleteData && rawProductData.variantsCompleteData[variantId]) {
                Logger.systemLog('ProductHelper', `Using full variant data preprocessed by the Liquid template: ${variantId}`);
                const completeVariantData = rawProductData.variantsCompleteData[variantId];

                // Normalize variantSKUs: convert SKU objects into SKU arrays when needed
                let variantSKUs = [];
                if (completeVariantData.variantSKUs) {
                    if (Array.isArray(completeVariantData.variantSKUs)) {
                        variantSKUs = completeVariantData.variantSKUs;
                    } else if (typeof completeVariantData.variantSKUs === 'object' && completeVariantData.variantSKUs.skuArray) {
                        // Convert the SKU object into an SKU array
                        variantSKUs = completeVariantData.variantSKUs.skuArray.map(item => ({
                            skuTitle: item.skuTitle || '',
                            skuQuantity: item.skuQuantity || 1,
                            variant: completeVariantData.variant,
                            subscription: completeVariantData.variantSubscriptions && completeVariantData.variantSubscriptions.length > 0 ? completeVariantData.variantSubscriptions[0] : null
                        }));
                    }
                }

                const result = {
                    ...baseStructure,
                    variant: completeVariantData.variant,
                    variantOptions: completeVariantData.variantOptions || [],
                    variantSKUs: variantSKUs,
                    variantSubscriptions: completeVariantData.variantSubscriptions || []
                };

                // Remove the internal variantsCompleteData field before returning
                delete result.variantsCompleteData;

                return result;
            }

            // Fall back to the legacy conversion path for backward compatibility
            Logger.warn('ProductHelper', `Preprocessed variant data not found, using fallback conversion logic: ${variantId}`);

            const originalVariants = rawProductData.productVariants || rawProductData.variants || [];
            const selectedVariant = originalVariants.find(v =>
                v.variantId && v.variantId.toString() === variantId.toString()
            );

            if (!selectedVariant) {
                throw new Error(`Variant ${variantId} not found in product`);
            }

            // Build the variant object
            const variantObj = {
                variantId: selectedVariant.variantId,
                variantTitle: selectedVariant.variantTitle,
                variantPrice: selectedVariant.price || 0,
                variantSKU: this.extractSKUString(selectedVariant.sku),
                variantCompareAtPrice: selectedVariant.compareAtPrice || null,
                variantPreOrderInfo: selectedVariant.preOrderInfo || ''
            };

            // Read the variant subscription data
            const variantSubscriptions = this.extractVariantSubscriptions(selectedVariant);

            // Build the full structure including variant information
            const fullStructure = {
                ...baseStructure,
                variant: variantObj,
                variantSKUs: this.parseVariantSKUs(selectedVariant.sku, variantObj, variantSubscriptions.length > 0 ? variantSubscriptions[0] : null),
                variantOptions: this.extractVariantOptionsFromVariant(selectedVariant, rawProductData.productOptions || rawProductData.options || []),
                variantSubscriptions: variantSubscriptions
            };

            // Remove the internal variantsCompleteData field before returning
            delete fullStructure.variantsCompleteData;

            return fullStructure;
        }

        /**
         * Clean the product variant array by removing nested structures and keeping a flat shape.
         * @param {Array} variants - Raw variant array.
         * @returns {Array} Cleaned variant array.
         */
        static cleanProductVariants(variants) {
            if (!Array.isArray(variants)) return [];

            return variants.map(variant => ({
                variantId: variant.variantId,
                variantTitle: variant.variantTitle,
                variantPrice: variant.price || 0,
                variantSKU: this.extractSKUString(variant.sku),
                variantCompareAtPrice: variant.compareAtPrice || null,
                variantPreOrderInfo: variant.preOrderInfo || ''
            }));
        }

        /**
         * Clean the product option array by flattening option values.
         * @param {Array} options - Raw option array.
         * @returns {Array} Cleaned option array.
         */
        static cleanProductOptions(options) {
            if (!Array.isArray(options)) return [];

            return options.map(option => ({
                optionName: option.optionName,
                optionValues: Array.isArray(option.optionValues)
                    ? option.optionValues.join('|')
                    : (option.optionValues || '')
            }));
        }

        /**
         * Extract the SKU string.
         * @param {string|Object} sku - SKU string or object.
         * @returns {string} SKU string.
         */
        static extractSKUString(sku) {
            if (typeof sku === 'string') {
                return sku;
            }
            if (typeof sku === 'object' && sku.originalSKU) {
                return sku.originalSKU;
            }
            return '';
        }

        /**
         * Parse variant SKUs into an SKU array that matches the new data structure.
         * @param {string|Object} sku - SKU string or object.
         * @param {Object} variant - Related variant object, optional.
         * @param {Object} subscription - Related subscription object, optional.
         * @returns {Array} SKU array.
         */
        static parseVariantSKUs(sku, variant = null, subscription = null) {
            if (!sku) return [];

            // Handle object format from the legacy structure
            if (typeof sku === 'object' && sku.skuArray) {
                return sku.skuArray.map(item => ({
                    skuTitle: item.skuTitle || '',
                    skuQuantity: item.skuQuantity || 1,
                    variant: variant,
                    subscription: subscription
                }));
            }

            // Handle string format from the new structure
            if (typeof sku === 'string') {
                // Parse formats like "PL-PC001-01W*2&PL-PC002-01B"
                const skuParts = sku.split('&');
                return skuParts.map(part => {
                    const [skuTitle, quantity] = part.split('*');
                    return {
                        skuTitle: skuTitle.trim(),
                        skuQuantity: parseInt(quantity) || 1,
                        variant: variant,
                        subscription: subscription
                    };
                });
            }

            return [];
        }

        /**
         * Extract option data from a variant for root-level variantOptions.
         * @param {Object} variant - Variant object.
         * @param {Array} productOptions - Product option array.
         * @returns {Array} Variant option array.
         */
        static extractVariantOptionsFromVariant(variant, productOptions) {
            const variantOptions = [];

            if (!productOptions || !Array.isArray(productOptions)) {
                return variantOptions;
            }

            productOptions.forEach((option, index) => {
                let optionValue = '';

                // Prefer Shopify's native option1, option2, and option3 fields
                const optionKey = `option${index + 1}`;
                if (variant[optionKey]) {
                    optionValue = variant[optionKey];
                }
                // Fallback: derive values from variantTitle
                else if (variant.variantTitle) {
                    const titleParts = variant.variantTitle.split(' / ');
                    if (titleParts[index]) {
                        optionValue = titleParts[index];
                    }
                }

                if (optionValue) {
                    variantOptions.push({
                        optionName: option.optionName,
                        optionValues: optionValue
                    });
                }
            });

            return variantOptions;
        }

        /**
         * Extract subscription data from a variant for root-level variantSubscriptions.
         * @param {Object} variant - Variant object.
         * @returns {Array} Variant subscription array.
         */
        static extractVariantSubscriptions(variant) {
            // Return subscription data when the variant already contains it
            if (variant.subscriptions && Array.isArray(variant.subscriptions)) {
                return variant.subscriptions;
            }

            // Otherwise return an empty array
            return [];
        }

        /**
         * Extract variant options while preserving backward compatibility.
         * @param {Object} variant - Variant object.
         * @param {Array} productOptions - Product option array.
         * @returns {Array} Variant option array.
         */
        static extractVariantOptions(variant, productOptions) {
            // If variantOptions already exists in the new format, return it directly
            if (variant.variantOptions && Array.isArray(variant.variantOptions)) {
                return variant.variantOptions;
            }

            const variantOptions = [];

            // Handle optionNameValues from the legacy format
            if (variant.optionNameValues && Array.isArray(variant.optionNameValues)) {
                productOptions.forEach(option => {
                    const matchingValue = variant.optionNameValues.find(value =>
                        value.startsWith(option.optionName + '_')
                    );
                    if (matchingValue) {
                        const optionValue = matchingValue.replace(option.optionName + '_', '');
                        variantOptions.push({
                            optionName: option.optionName,
                            optionValues: optionValue
                        });
                    }
                });
            }
            // Handle optionValues from the legacy array format
            else if (variant.optionValues && Array.isArray(variant.optionValues)) {
                variant.optionValues.forEach((optionObj, index) => {
                    if (optionObj.optionName && optionObj.optionValues) {
                        // Support both the new string format and the legacy array format
                        const optionValue = Array.isArray(optionObj.optionValues)
                            ? optionObj.optionValues[0]
                            : optionObj.optionValues;
                        variantOptions.push({
                            optionName: optionObj.optionName,
                            optionValues: optionValue
                        });
                    }
                });
            }

            return variantOptions;
        }

        /**
         * Convert a product variant into the legacy variant structure.
         * Deprecated and kept only for compatibility.
         * @param {Object} productData - Product data object.
         * @param {string|number} variantId - Variant ID.
         * @returns {Object|null} Variant structure object, or null when not found.
         */
        static convertProductVariantToVariantStructure(productData, variantId) {
            if (!productData || !productData.variants || !variantId) {
                return null;
            }

            // Find the matching variant inside the product variants array
            const variant = productData.variants.find(v =>
                v.variantId && v.variantId.toString() === variantId.toString()
            );

            if (!variant) {
                return null;
            }

            // Build the variant structure
            const variantStructure = {
                variantId: variant.variantId,
                variantTitle: variant.variantTitle,
                optionValues: variant.optionValues || [],
                optionNameValues: variant.optionNameValues || [],
                product: {
                    productId: productData.productId,
                    productTitle: productData.productTitle,
                    productCategory: productData.productCategory,
                    collections: productData.collections || [],
                    productUrl: productData.productUrl,
                    options: productData.options || [],
                    variants: productData.variants || [],
                    hasSubscriptionVariant: productData.hasSubscriptionVariant || false,
                    hasPreOrderVariant: productData.hasPreOrderVariant || false
                },
                price: variant.price || 0,
                compareAtPrice: variant.compareAtPrice || null,
                sku: variant.sku || { originalSKU: '', skuArray: [], skuArrayWithoutQuantity: [] },
                subscriptions: variant.subscriptions || [],
                preOrderInfo: variant.preOrderInfo || ''
            };

            return variantStructure;
        }

        /**
         * Get product data by variant ID.
         * @param {string|number} variantId - Variant ID.
         * @returns {Promise<Object>} Product data object in the new structure.
         */
        static async getProductByVariantId(variantId) {

            try {
                // Use Shopify's variant API to fetch the variant data first
                const url = `/variants/${variantId}.js`;

                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const variantData = await response.json();

                // Read the product ID from the variant response
                const productId = variantData.product_id;

                // Fetch the full product payload by product ID
                const productUrl = `/products/${productId}.js`;

                const productResponse = await fetch(productUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (!productResponse.ok) {
                    throw new Error(`HTTP error! status: ${productResponse.status}`);
                }

                const productData = await productResponse.json();

                // Convert it into the new data structure
                const result = this.convertToNewProductStructure(productData, variantId);

                return result;
            } catch (error) {
                throw error;
            }
        }

        /**
         * Get variant data by product handle and variant ID.
         * The method fetches product data first, then converts it into the new structure.
         * @param {string} handle - Product handle identifier.
         * @param {string|number} variantId - Variant ID.
         * @returns {Promise<Object>} Variant data object in the new structure.
         */
        static async getVariantByHandle(handle, variantId) {

            if (!variantId) {
                throw new Error('Variant ID is required');
            }

            try {
                // If the handle is unavailable, fall back to fetching by variant ID
                if (!handle || typeof handle !== 'string') {
                    // Fetch variant data through Shopify, then use it to fetch product data
                    const productData = await this.getProductByVariantId(variantId);
                    return productData;
                }

                // Call getProductByHandle directly with the variantId
                // This returns the complete new structure including variant information
                const result = await this.getProductByHandle(handle, variantId);
                return result;
            } catch (error) {
                console.error('Error fetching variant by handle:', error);
                throw error;
            }
        }

        /**
         * Get all variant data for a product from the productVariants array in the new structure.
         * @param {string} handle - Product handle identifier.
         * @returns {Promise<Array>} Array of all variant data.
         */
        static async getAllVariantsByHandle(handle) {
            try {
                const productData = await this.getProductByHandle(handle);
                return productData.productVariants || [];
            } catch (error) {
                console.error('Error fetching all variants by handle:', error);
                throw error;
            }
        }

        /**
         * Get cart data.
         * Prefer DOM mount points maintained by cart sections and the global cart component.
         *
         * Cache strategy:
         * - `main-cart-items.liquid` and `cart-drawer.liquid` render cart data into the DOM automatically
         * - `dl-cart-ll.liquid` exposes raw cart items through `#cart-item-json`
         * - When no DOM mount point exists, fall back to the original AJAX request for compatibility
         *
         * @returns {Promise<Object>} Cart data object.
         */
        static async getCartData() {
            const scriptId = 'pixel-cart-data';
            const legacyScriptId = 'cart-item-json';

            try {
                // 1. Prefer normalized cart data already cached in the DOM by cart sections
                const cachedData = this.getDataFromScript(scriptId);
                if (this.isNormalizedCartData(cachedData)) {
                    Logger.systemLog('ProductHelper', `Resolved cart data from DOM cache: ${cachedData.lineItems.length} items`);
                    return cachedData;
                }

                // 2. Fall back to the global cart mount point and normalize the raw cart items
                const legacyCartItems = this.getDataFromScript(legacyScriptId);
                if (Array.isArray(legacyCartItems)) {
                    const normalizedCartData = this.convertLegacyCartItemsToCartData(legacyCartItems);
                    if (normalizedCartData) {
                        Logger.systemLog('ProductHelper', `Resolved cart data from legacy DOM cache: ${normalizedCartData.lineItems.length} items`);
                        this.saveDataToScript(scriptId, normalizedCartData);
                        return normalizedCartData;
                    }
                }

                // 3. Preserve the original AJAX fallback when no DOM mount point is available
                Logger.systemLog('ProductHelper', 'Cart DOM mount points not found, fetching cart data via AJAX');
                const url = '/cart?view=pixel';
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const cartData = await response.json();

                // Persist the fetched data into the DOM script tag cache
                this.saveDataToScript(scriptId, cartData);

                return cartData;
            } catch (error) {
                Logger.error('ProductHelper', 'Error fetching cart data:', error);
                throw error;
            }
        }

        /**
         * Get all variant data from the cart.
         * @returns {Promise<Array>} Array of all variants in the cart.
         */
        static async getCartVariants() {
            try {
                const cartData = await this.getCartData();
                if (!cartData || !cartData.lineItems) {
                    return [];
                }

                return cartData.lineItems.map(item => item.variant);
            } catch (error) {
                console.error('Error fetching cart variants:', error);
                throw error;
            }
        }

        /**
 * Get cart summary information.
 * @returns {Promise<Object>} Cart summary object.
 */
        static async getCartSummary() {
            try {
                const cartData = await this.getCartData();
                if (!cartData) {
                    return null;
                }

                // Normalize the new cart structure: lineItemsSummary + discounts
                const summary = cartData.lineItemsSummary || {};
                return {
                    totalQuantity: summary.totalQuantity || 0,
                    totalItem: summary.totalItem || 0,
                    totalOriginalPrice: summary.totalOriginalPrice || 0,
                    totalFinalPrice: summary.totalFinalPrice || 0,
                    totalDiscountPrice: summary.totalDiscountPrice || 0,
                    allDiscounts: cartData.discounts || []
                };
            } catch (error) {
                Logger.error('ProductHelper', 'Error fetching cart summary:', error);
                throw error;
            }
        }

            /**
             * Get a cart line item by variant ID.
             * @param {string|number} variantId - Variant ID.
             * @returns {Promise<Object|null>} Matching line item, or null when not found.
             */
        static async getCartLineItemByVariantId(variantId) {
            try {
                const cartData = await this.getCartData();
                if (!cartData || !cartData.lineItems) {
                    return null;
                }

                const lineItem = cartData.lineItems.find(item =>
                    item.variant && item.variant.variantId &&
                    item.variant.variantId.toString() === variantId.toString()
                );

                return lineItem || null;
            } catch (error) {
                console.error('Error fetching cart line item:', error);
                throw error;
            }
        }

        /**
         * Clear cached cart data.
         */
        static clearCartCache() {
            try {
                const cartScript = document.getElementById('pixel-cart-data');
                if (cartScript) {
                    cartScript.remove();
                }
            } catch (error) {
                console.warn('Failed to clear cart cache:', error);
            }
        }

        /**
         * Get collection data by collection handle.
         * @param {string} handle - Collection handle.
         * @returns {Promise<Object>} Collection data object.
         */
        static async getCollectionByHandle(handle) {
            if (!handle) {
                throw new Error('Collection handle is required');
            }

            const scriptId = `pixel-collection-${handle}`;

            try {
                // Try DOM cache first
                let collectionData = this.getDataFromScript(scriptId);
                if (collectionData) {
                    return collectionData;
                }

                // Fetch via AJAX when no cache is available
                const response = await fetch(`/collections/${handle}?view=pixel`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch collection: ${response.status}`);
                }

                collectionData = await response.json();

                // Save the result back into the DOM cache
                this.saveDataToScript(scriptId, collectionData);

                return collectionData;
            } catch (error) {
                console.error('Error fetching collection by handle:', error);
                throw error;
            }
        }

        /**
         * Clear cached collection data.
         * @param {string} handle - Collection handle, optional.
         */
        static clearCollectionCache(handle) {
            try {
                if (handle) {
                    const collectionScript = document.getElementById(`pixel-collection-${handle}`);
                    if (collectionScript) {
                        collectionScript.remove();
                    }
                } else {
                    // Clear all collection cache entries
                    const collectionScripts = document.querySelectorAll('[id^="pixel-collection-"]');
                    collectionScripts.forEach(script => script.remove());
                }
            } catch (error) {
                console.warn('Failed to clear collection cache:', error);
            }
        }

        /**
         * Clear all cached data, including product, cart, and collection caches.
         */
        static clearAllCache() {
            try {
                this.clearProductCache();
                this.clearCartCache();
                this.clearCollectionCache();

                // Clear any other pixel-related cache nodes that may still exist
                const allPixelScripts = document.querySelectorAll('[id^="pixel-"]');
                allPixelScripts.forEach(script => script.remove());
            } catch (error) {
                console.warn('Failed to clear all cache:', error);
            }
        }

    };

    /**
     * CartHelper - cart data cleanup and preparation utility class (inner class of DLPixelDataHelper).
     */
    static CartHelper = class CartHelper {

        /**
         * Clean a LineItem object and remove internal analysis fields.
         * @param {Object} lineItem - Raw LineItem object.
         * @returns {Object} Cleaned LineItem object.
         */
        static cleanLineItem(lineItem) {
            if (!lineItem) return null;

            return {
                variant: lineItem.variant,
                variantSubscription: lineItem.variantSubscription,
                product: lineItem.product,
                lineItemDiscount: lineItem.lineItemDiscount,
                lineItemQuantity: lineItem.lineItemQuantity,
                lineItemPrice: lineItem.lineItemPrice,
                lineItemFinalPrice: lineItem.lineItemFinalPrice
                // Removed: isNewItem, isQuantityIncrease, previousQuantity
            };
        }

        /**
         * Clean an array of LineItem objects.
         * @param {Array} lineItems - LineItem array.
         * @returns {Array} Cleaned LineItem array.
         */
        static cleanLineItems(lineItems) {
            if (!Array.isArray(lineItems)) return [];
            return lineItems.map(item => this.cleanLineItem(item)).filter(item => item !== null);
        }

        /**
         * Generate the lineItemSKUs array from a single LineItem.
         * @param {Object} lineItem - LineItem object.
         * @returns {Array} SKU array.
         */
        static generateLineItemSKUs(lineItem) {
            if (!lineItem || !lineItem.variant || !lineItem.variant.variantSKU) {
                return [];
            }

            return DLPixelDataHelper.ProductHelper.parseVariantSKUs(
                lineItem.variant.variantSKU,
                lineItem.variant,
                lineItem.variantSubscription
            );
        }

        /**
         * Generate the lineItemDiscounts array from a single LineItem.
         * @param {Object} lineItem - LineItem object.
         * @returns {Array} Discount array.
         */
        static generateLineItemDiscounts(lineItem) {
            if (!lineItem || !lineItem.lineItemDiscount) {
                return [];
            }

            return [lineItem.lineItemDiscount];
        }

        /**
         * Generate aggregated summarySKUs from multiple LineItems.
         * @param {Array} lineItems - LineItem array.
         * @returns {Array} Aggregated SKU array.
         */
        static generateSummarySKUs(lineItems) {
            if (!Array.isArray(lineItems)) return [];

            const skuMap = new Map(); // Used to deduplicate SKUs and merge their quantities

            lineItems.forEach(item => {
                if (item.variant && item.variant.variantSKU) {
                    const itemSKUs = DLPixelDataHelper.ProductHelper.parseVariantSKUs(
                        item.variant.variantSKU,
                        item.variant,
                        item.variantSubscription
                    );

                    itemSKUs.forEach(sku => {
                        const key = `${sku.skuTitle}_${sku.variant?.variantId || 'no_variant'}`;
                        if (skuMap.has(key)) {
                            // Merge quantities for the same SKU
                            const existing = skuMap.get(key);
                            existing.skuQuantity += sku.skuQuantity * item.lineItemQuantity;
                        } else {
                            // New SKU entry, multiplied by the line item quantity
                            skuMap.set(key, {
                                skuTitle: sku.skuTitle,
                                skuQuantity: sku.skuQuantity * item.lineItemQuantity,
                                variant: sku.variant,
                                subscription: sku.subscription
                            });
                        }
                    });
                }
            });

            // Convert the map back into an array
            return Array.from(skuMap.values());
        }

        /**
         * Generate a deduplicated discount array from multiple LineItems.
         * @param {Array} lineItems - LineItem array.
         * @returns {Array} Discount array.
         */
        static generateDiscounts(lineItems) {
            if (!Array.isArray(lineItems)) return [];

            const discounts = [];
            lineItems.forEach(item => {
                if (item.lineItemDiscount && item.lineItemDiscount.discountTitle) {
                    // Skip duplicate discounts that were already added
                    const existingDiscount = discounts.find(d =>
                        d.discountTitle === item.lineItemDiscount.discountTitle &&
                        d.discountType === item.lineItemDiscount.discountType
                    );

                    if (!existingDiscount) {
                        discounts.push({
                            discountTitle: item.lineItemDiscount.discountTitle,
                            discountType: item.lineItemDiscount.discountType,
                            discountValue: item.lineItemDiscount.discountValue,
                            discountApplicationType: item.lineItemDiscount.discountApplicationType,
                            discountApplicationValueType: item.lineItemDiscount.discountApplicationValueType,
                            discountApplicationValue: item.lineItemDiscount.discountApplicationValue,
                            discountTargetSelection: item.lineItemDiscount.discountTargetSelection
                        });
                    }
                }
            });

            return discounts;
        }

        /**
         * Build the full event payload for a single product add-to-cart action.
         * @param {Object} lineItem - LineItem object.
         * @param {Object} context - Per-item tracking context.
         * @returns {Object} Full event payload.
         */
        static getProductAddedToCartItemData(lineItem, context = {}) {
            const eventData = {
                lineItem: this.cleanLineItem(lineItem),
                lineItemSKUs: this.generateLineItemSKUs(lineItem),
                lineItemDiscounts: this.generateLineItemDiscounts(lineItem),
                cartSource: context.cartSource || 'other'
            };

            if (context.parentLineItem) {
                eventData.parentLineItem = String(context.parentLineItem);
            }
            if (context.parentLineItemSkus) {
                eventData.parentLineItemSkus = String(context.parentLineItemSkus);
            }

            return eventData;
        }

        /**
         * Get the complete summary payload for the current cart.
         * @returns {Promise<Object|null>} Full cart data structure including summary, summarySKUs, lineItems, and discounts.
         */
        static async getCartSummaryData() {
            try {
                // Read the current cart data
                const cartData = await DLPixelDataHelper.ProductHelper.getCartData();
                if (!cartData || !cartData.lineItems || cartData.lineItems.length === 0) {
                    return null;
                }

                // Read the cart summary data
                const cartSummary = await DLPixelDataHelper.ProductHelper.getCartSummary();
                if (!cartSummary) {
                    return null;
                }

                // Build the summary object
                const summary = {
                    totalQuantity: cartSummary.totalQuantity || 0,
                    totalItem: cartSummary.totalItem || 0,
                    totalOriginalPrice: cartSummary.totalOriginalPrice || 0,
                    totalFinalPrice: cartSummary.totalFinalPrice || 0,
                    totalDiscountPrice: cartSummary.totalDiscountPrice || 0
                };

                // Clean the lineItems payload
                const cleanLineItems = this.cleanLineItems(cartData.lineItems);

                // Generate summarySKUs
                const summarySKUs = this.generateSummarySKUs(cartData.lineItems);

                // Build discounts including both cart-level and line-item-level discounts
                const discounts = [
                    ...this.generateDiscounts(cartData.lineItems), // Line-item-level discounts
                    ...(cartSummary.allDiscounts || []) // Cart-level discounts
                ];

                return {
                    summary: summary,
                    summarySKUs: summarySKUs,
                    lineItems: cleanLineItems,
                    discounts: discounts
                };

            } catch (error) {
                Logger.error('CartHelper', 'getCartSummaryData failed to resolve cart summary data:', error);
                return null;
            }
        }

        /**
         * Build the full event payload for the aggregated add-to-cart event.
         * @param {Object} summary - LineItemsSummary object.
         * @param {Array} lineItems - LineItem array.
         * @returns {Object} Full event payload.
         */
        static getProductAddedToCartData(summary, lineItems = []) {
            return {
                summary: summary,
                summarySKUs: this.generateSummarySKUs(lineItems),
                lineItems: this.cleanLineItems(lineItems),
                discounts: this.generateDiscounts(lineItems)
            };
        }

    };



    /**
     * Initialize DLPixelDataHelper (private method).
     * Runs landing page detection and maintenance logic.
     */
    static _init() {
        try {
            // Run automatic landing page detection and persistence
            this.URLHelper.autoDetectAndStoreLandingPage();

            // Additional initialization logic can live here
            // For example, cleanup of expired data
            this._cleanupExpiredData();
        } catch (error) {
            // Swallow the error silently
        }
    }

    /**
     * Clean up expired landing page data (private method).
     * @param {number} maxDays - Maximum retention period in days, default is 30.
     */
    static _cleanupExpiredData(maxDays = 30) {
        try {
            if (this.URLHelper.isLandingPageInfoExpired(maxDays)) {
                this.URLHelper.clearLandingPageInfo();
            }
        } catch (error) {
            // Swallow the error silently
        }
    }
}

// Auto-initialize
DLPixelH.init();

// Export to the global scope
if (typeof window !== 'undefined') {
    window.DLPixelH = DLPixelH;
    window.DLPixelDataHelper = DLPixelDataHelper;

    // Export the ecommerce event helper
    window.ECommerceEventHelper = DLPixelH.ECommerceEventHelper;

    // Export internal helper classes to the global scope for backward compatibility
    window.URLHelper = DLPixelDataHelper.URLHelper;
    window.PageHelper = DLPixelDataHelper.PageHelper;
    window.ProductHelper = DLPixelDataHelper.ProductHelper;
    window.CartHelper = DLPixelDataHelper.CartHelper;
    window.Logger = Logger;
}

// Initialize the data helper, including landing page detection and maintenance
DLPixelDataHelper._init();
